<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { t } from '../i18n';
import {
  fetchMap, fetchPosition, moveTo,
  type MapData, type DistrictInfo, type MobData, type Nerve,
} from '../game/map-api';
import { mobImg, crestImg, classImg, crestArt, mobArt } from '../game/art';
// Mythic portraits ship as real mob art (mobs/<type>.webp: fenrir-spawn,
// jormungandr-coil, draugr-king, frost-jotunn) — the kingpin icon remains only
// as a safety net should a mythic id ever miss its file.
const mythicPortraitFallback = mobArt('kingpin');
import * as sfx from '../game/sfx';
import { toast } from '../game/toast';
import cashIcon from '../assets/fimbulwake/icons/cash.webp';

/** Head-biased square crop of a full-body portrait, clipped into a disc.
 *  The stylized-3D character art is full-body on dark charcoal — the face sits
 *  in the upper ~quarter, so we crop a 56%-width square starting 6% down. */
function drawPortraitDisc(
  ctx: CanvasRenderingContext2D, im: HTMLImageElement,
  cx: number, cy: number, rad: number,
  wide = false,
) {
  const w = im.naturalWidth, h = im.naturalHeight;
  // The default window is a HEADSHOT crop tuned for humanoid bust art (56% of
  // the width, off the top). Creature art (elites' beast stand-ins, mythics)
  // is full-body and often wider than it is tall about the head — the narrow
  // window beheads a wolf. `wide` opens the window to the full width so the
  // whole head and shoulders land inside the disc.
  const s = wide ? Math.min(w, h * 0.66) : Math.min(w * 0.56, h);
  const sx = (w - s) / 2;
  const sy = Math.min(h * (wide ? 0.03 : 0.06), h - s);
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, rad, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  // dark felt behind the cutout so charcoal edges melt into the disc
  ctx.fillStyle = '#0e1622';
  ctx.fill();
  ctx.drawImage(im, sx, sy, s, s, cx - rad, cy - rad, rad * 2, rad * 2);
  ctx.restore();
}

/** Slim layered "brass seat" ring: dark outer seat, metal band with a top
 *  sheen, dark inner hairline. Band colour = family accent (or brass). */
function drawDecoRing(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, rad: number, band: string | CanvasGradient,
) {
  const lw = Math.min(Math.max(1.2, rad * 0.09), 2.4);
  // dark seat just outside the band grounds the disc on any tile colour
  ctx.beginPath();
  ctx.arc(cx, cy, rad + lw * 0.9, 0, Math.PI * 2);
  ctx.lineWidth = lw * 1.1;
  ctx.strokeStyle = 'rgba(6,5,10,0.85)';
  ctx.stroke();
  // the metal band
  ctx.beginPath();
  ctx.arc(cx, cy, rad, 0, Math.PI * 2);
  ctx.lineWidth = lw;
  ctx.strokeStyle = band;
  ctx.stroke();
  // top sheen — makes the band read as turned metal, not a flat outline
  const sheen = ctx.createLinearGradient(cx, cy - rad, cx, cy + rad);
  sheen.addColorStop(0, 'rgba(255,244,214,0.55)');
  sheen.addColorStop(0.45, 'rgba(255,244,214,0.06)');
  sheen.addColorStop(1, 'rgba(0,0,0,0.28)');
  ctx.beginPath();
  ctx.arc(cx, cy, rad, 0, Math.PI * 2);
  ctx.lineWidth = lw;
  ctx.strokeStyle = sheen;
  ctx.stroke();
  // inner hairline settles the portrait into its bezel
  ctx.beginPath();
  ctx.arc(cx, cy, rad - lw * 0.8, 0, Math.PI * 2);
  ctx.lineWidth = Math.max(0.8, lw * 0.45);
  ctx.strokeStyle = 'rgba(9, 15, 24,0.8)';
  ctx.stroke();
}

/** Gold/aurora pulse ring marking a MYTHIC world boss — distinct at a glance
 *  from the standard family-accent bezel (drawDecoRing above). Placeholder
 *  art: the portrait itself just reuses an existing icon (see the
 *  `mythic art placeholder` comment at the icon-resolution site) — only this
 *  ring + glow is bespoke until a dedicated mythic art pass ships. */
function drawMythicRing(ctx: CanvasRenderingContext2D, cx: number, cy: number, rad: number, t: number) {
  const pulse = 1 + 0.08 * Math.sin(t / 400);
  const glow = ctx.createRadialGradient(cx, cy, rad * 0.8, cx, cy, rad * 1.9);
  glow.addColorStop(0, 'rgba(255,214,120,0.5)');
  glow.addColorStop(0.55, 'rgba(160,255,225,0.18)');
  glow.addColorStop(1, 'rgba(160,255,225,0)');
  ctx.beginPath();
  ctx.arc(cx, cy, rad * 1.9, 0, Math.PI * 2);
  ctx.fillStyle = glow;
  ctx.fill();
  const band = ctx.createLinearGradient(cx, cy - rad, cx, cy + rad);
  band.addColorStop(0, '#fff2c0');
  band.addColorStop(0.5, '#e8b73f');
  band.addColorStop(1, '#a9720f');
  ctx.beginPath();
  ctx.arc(cx, cy, rad * pulse, 0, Math.PI * 2);
  ctx.lineWidth = Math.min(Math.max(1.6, rad * 0.14), 3.2);
  ctx.strokeStyle = band;
  ctx.stroke();
}

import nerveIcon from '../assets/fimbulwake/icons/nerve.webp';
import { districtAccent, accentRgba } from '../game/district-accent';
import FightScreen from './FightScreen.vue';
import { startFight, startGarrison, startDefend, type FightView } from '../game/mobfight-api';
import { useRegenClock, fmtEta } from '../game/regen-clock';
import QuickRecover from './QuickRecover.vue';

const props = defineProps<{
  heroClass?: string; heroLevel?: number; heroId?: string;
  heroHp?: number; heroMaxHp?: number; heroFullHp?: number; cash?: number; goldBars?: number;
  heroHpRegen?: { exact: number; perMin: number };
  /** District to smoothly pan the camera to on open ("Take it on the map"). */
  focusDistrict?: string | null;
}>();
const emit = defineEmits<{
  (e: 'nerve', n: Nerve): void;
  (e: 'updated'): void;
  (e: 'focused'): void;
  /** "Take me to the Wards" from the conquest card — the shell owns navigation. */
  (e: 'wards'): void;
}>();

function goToWards() {
  takenNotice.value = null;
  emit('wards');
}

/** A quick-recover purchase changed nerve/HP/cash — refresh the HUD + parent. */
async function onRecovered() {
  try {
    const p = await fetchPosition();
    nerve.value = p.nerve;
    emit('nerve', p.nerve);
  } catch { /* HUD refreshes on the next move anyway */ }
  emit('updated');
}

// ── Hex geometry (ported from dominion HexMap: pointy-top, odd-row offset) ──
const HEX_R = 22;
const HEX_W = Math.sqrt(3) * HEX_R; // ~38.1 px

// ── WINTER terrain palette. The old values were Omerta's CITY verbatim — warm
// brown blocks, olive slums, dark asphalt — which is why the map still read as
// the mafia game however the chrome changed. This hold is UNDER SNOW: packed-snow
// paths are the LIGHTEST thing on the board (paths through snow are), the river
// is frozen pale ice, park is spruce, warzone is blood on snow.
// (same layout semantics: block=grass street=road park=forest slums=swamp
//  rubble=ruins warzone=undead highrise=mountain stronghold=territory) ──
const TILE_COLOR: Record<string, string> = {
  block: '#8b98a6', park: '#33544a', highrise: '#2a3140', river: '#7fa8c6',
  bridge: '#8a6f43', slums: '#5d6673', rubble: '#6b7482', stronghold: '#131720',
  street: '#b9c6d2', warzone: '#6e2f36',
};
const TILE_BORDER: Record<string, string> = {
  block: '#76828f', park: '#274238', highrise: '#414b5e', river: '#5f8cab',
  bridge: '#755a32', slums: '#4d5560', rubble: '#59616e', stronghold: '#c9913a',
  street: '#9dabb9', warzone: '#57232a',
};
const TILE_COST: Record<string, number> = {
  block: 1, street: 1, stronghold: 1, bridge: 1, rubble: 1,
  park: 2, slums: 2, warzone: 2,
  highrise: Infinity, river: Infinity,
};
const IMPASSABLE = new Set(['highrise', 'river']);

// ── Tile stamps — the relief pass ───────────────────────────────────────────
// A flat fill per hex is why the board read as "2000s browser game": no grain,
// no light, no height. Each terrain type is pre-rendered ONCE into 4 offscreen
// stamp variants (NW-lit gradient, speckle, hand-drawn glyphs — spruces, crags,
// ice cracks, blood on snow), then the draw loop blits them. Raised terrain
// (spruce, crags) pokes ABOVE its hex into the stamp's headroom; the row loop
// already paints top-to-bottom, so lower tiles correctly occlude the bases —
// painter's algorithm for free.
const STAMP_W = 40;         // world units; hex is ~38.1 wide
const STAMP_HEAD = 16;      // headroom above the hex for treetops / peaks
const STAMP_H = STAMP_HEAD + HEX_R * 2; // 60
const STAMP_SCALE = 3;      // crisp when zoomed in
let tileStamps: Record<string, HTMLCanvasElement[]> | null = null;

/** Deterministic per-hex hash → stamp variant. Random-per-frame would shimmer. */
function tileHash(col: number, row: number): number {
  let h = (col * 374761393 + row * 668265263) | 0;
  h = ((h ^ (h >>> 13)) * 1274126177) | 0;
  return (h ^ (h >>> 16)) >>> 0;
}
function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
/** Multiply a #rrggbb by f (clamped) — cheap lighten/darken for the lit gradient. */
function shade(hex: string, f: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.min(255, Math.round(((n >> 16) & 255) * f));
  const g = Math.min(255, Math.round(((n >> 8) & 255) * f));
  const b = Math.min(255, Math.round((n & 255) * f));
  return `rgb(${r},${g},${b})`;
}

function makeStamp(type: string, variant: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = STAMP_W * STAMP_SCALE;
  c.height = STAMP_H * STAMP_SCALE;
  const g = c.getContext('2d')!;
  g.scale(STAMP_SCALE, STAMP_SCALE);
  const rand = mulberry32(0xf1b0 + type.charCodeAt(0) * 977 + type.length * 131 + variant * 7919);
  const cx = STAMP_W / 2;
  const cy = STAMP_HEAD + HEX_R;
  const base = TILE_COLOR[type] ?? '#4f4a42';
  const jitter = 0.96 + rand() * 0.08; // per-variant brightness spread

  // Base: NW-lit gradient instead of a flat swatch.
  const lg = g.createLinearGradient(cx - HEX_R, cy - HEX_R, cx + HEX_R, cy + HEX_R);
  lg.addColorStop(0, shade(base, 1.09 * jitter));
  lg.addColorStop(1, shade(base, 0.85 * jitter));
  hexPath(g, cx, cy, HEX_R - 0.5);
  g.fillStyle = lg;
  g.fill();

  // Interior detail, clipped to the hex.
  g.save();
  hexPath(g, cx, cy, HEX_R - 0.5);
  g.clip();

  // grain speckle — breaks the vector-flat read at any zoom
  for (let i = 0; i < 26; i++) {
    const x = cx + (rand() - 0.5) * HEX_R * 1.8;
    const y = cy + (rand() - 0.5) * HEX_R * 1.8;
    g.fillStyle = rand() < 0.5 ? `rgba(255,255,255,${0.03 + rand() * 0.07})` : `rgba(0,0,10,${0.04 + rand() * 0.08})`;
    g.fillRect(x, y, 0.9 + rand(), 0.9 + rand());
  }

  switch (type) {
    case 'block': { // snowfield — drift curves, an odd pebble
      for (let i = 0; i < 2; i++) {
        const y = cy - 8 + i * 11 + rand() * 4;
        g.beginPath();
        g.moveTo(cx - 16, y);
        g.quadraticCurveTo(cx - 4 + rand() * 8, y - 4 - rand() * 3, cx + 15, y + rand() * 2);
        g.strokeStyle = 'rgba(255,255,255,0.16)';
        g.lineWidth = 1.1;
        g.stroke();
        g.beginPath();
        g.moveTo(cx - 16, y + 1.4);
        g.quadraticCurveTo(cx - 4 + rand() * 8, y - 2.6 - rand() * 3, cx + 15, y + 1.4 + rand() * 2);
        g.strokeStyle = 'rgba(20,30,45,0.14)';
        g.stroke();
      }
      if (rand() < 0.5) {
        const px = cx + (rand() - 0.5) * 20, py = cy + (rand() - 0.5) * 16;
        g.fillStyle = 'rgba(52,62,74,0.9)';
        g.fillRect(px, py, 2.6, 1.8);
        g.fillStyle = 'rgba(230,240,250,0.5)';
        g.fillRect(px, py - 0.7, 2.6, 0.7);
      }
      break;
    }
    case 'street': { // packed path — sled ruts and boot prints
      for (let i = 0; i < 2; i++) {
        const y = cy - 5 + i * 9;
        g.beginPath();
        g.moveTo(cx - 17, y + rand() * 3);
        g.quadraticCurveTo(cx, y - 2 + rand() * 4, cx + 17, y + rand() * 3);
        g.strokeStyle = 'rgba(70,86,102,0.35)';
        g.lineWidth = 1.4;
        g.stroke();
      }
      for (let i = 0; i < 5; i++) {
        g.fillStyle = 'rgba(60,76,92,0.4)';
        g.fillRect(cx + (rand() - 0.5) * 26, cy + (rand() - 0.5) * 22, 1.6, 2.4);
      }
      break;
    }
    case 'river': { // frozen river — pressure cracks and a cold sheen
      const sheen = g.createRadialGradient(cx - 6, cy - 8, 2, cx, cy, HEX_R * 1.4);
      sheen.addColorStop(0, 'rgba(235,248,255,0.28)');
      sheen.addColorStop(1, 'rgba(235,248,255,0)');
      g.fillStyle = sheen;
      g.fillRect(cx - HEX_R, cy - HEX_R, HEX_R * 2, HEX_R * 2);
      for (let i = 0; i < 3; i++) {
        g.beginPath();
        let x = cx - 14 + rand() * 6, y = cy - 12 + rand() * 24;
        g.moveTo(x, y);
        for (let s = 0; s < 3; s++) {
          x += 7 + rand() * 5;
          y += (rand() - 0.5) * 9;
          g.lineTo(x, y);
        }
        g.strokeStyle = 'rgba(226,244,255,0.4)';
        g.lineWidth = 0.8;
        g.stroke();
        g.strokeStyle = 'rgba(38,74,104,0.45)';
        g.lineWidth = 0.6;
        g.stroke();
      }
      break;
    }
    case 'bridge': { // timber span — planks and two rope rails
      for (let i = 0; i < 5; i++) {
        const y = cy - 14 + i * 7 + rand() * 2;
        g.strokeStyle = i % 2 ? 'rgba(46,32,14,0.5)' : 'rgba(225,205,160,0.16)';
        g.lineWidth = i % 2 ? 1.2 : 0.8;
        g.beginPath();
        g.moveTo(cx - 17, y);
        g.lineTo(cx + 17, y);
        g.stroke();
      }
      g.strokeStyle = 'rgba(30,22,10,0.6)';
      g.lineWidth = 1.4;
      for (const dy of [-16, 16]) {
        g.beginPath();
        g.moveTo(cx - 18, cy + dy);
        g.quadraticCurveTo(cx, cy + dy * 0.78, cx + 18, cy + dy);
        g.stroke();
      }
      break;
    }
    case 'slums': { // frozen marsh — dead reeds in the ice
      for (let i = 0; i < 6; i++) {
        const x = cx + (rand() - 0.5) * 30, y = cy + (rand() - 0.5) * 24;
        const h = 4 + rand() * 5, lean = (rand() - 0.5) * 3;
        g.beginPath();
        g.moveTo(x, y);
        g.quadraticCurveTo(x + lean, y - h * 0.6, x + lean * 1.6, y - h);
        g.strokeStyle = 'rgba(24,32,26,0.55)';
        g.lineWidth = 1;
        g.stroke();
        g.fillStyle = 'rgba(205,218,220,0.35)';
        g.fillRect(x + lean * 1.6 - 0.6, y - h - 1.2, 1.2, 1.6);
      }
      break;
    }
    case 'rubble': { // fallen stones under snow
      for (let i = 0; i < 5; i++) {
        const x = cx + (rand() - 0.5) * 26, y = cy + (rand() - 0.5) * 22;
        const w = 3 + rand() * 4, h = 2 + rand() * 3;
        g.fillStyle = 'rgba(0,0,12,0.3)';
        g.fillRect(x - w / 2 + 0.8, y - h / 2 + 1, w, h); // cast shadow
        g.fillStyle = shade('#79828e', 0.85 + rand() * 0.3);
        g.fillRect(x - w / 2, y - h / 2, w, h);
        g.fillStyle = 'rgba(235,244,252,0.5)';
        g.fillRect(x - w / 2, y - h / 2, w, 1); // snow on top
      }
      break;
    }
    case 'warzone': { // blood on snow, scorched ground
      g.fillStyle = 'rgba(10,6,8,0.28)';
      g.beginPath();
      g.ellipse(cx + (rand() - 0.5) * 8, cy + (rand() - 0.5) * 8, 9 + rand() * 4, 6 + rand() * 3, rand(), 0, Math.PI * 2);
      g.fill();
      for (let i = 0; i < 4; i++) {
        g.fillStyle = `rgba(126,28,38,${0.4 + rand() * 0.35})`;
        g.beginPath();
        g.ellipse(cx + (rand() - 0.5) * 24, cy + (rand() - 0.5) * 20, 1.4 + rand() * 2.6, 1 + rand() * 1.8, rand() * 3, 0, Math.PI * 2);
        g.fill();
      }
      break;
    }
    case 'stronghold': { // dressed stone with one ember-lit arrow slit
      for (let ry = 0; ry < 6; ry++) { // full-hex coursework — the clip trims it
        const y = cy - 21 + ry * 8;
        g.strokeStyle = 'rgba(0,0,8,0.4)';
        g.lineWidth = 0.9;
        g.beginPath();
        g.moveTo(cx - 17, y);
        g.lineTo(cx + 17, y);
        g.stroke();
        g.strokeStyle = 'rgba(210,226,240,0.07)';
        g.beginPath();
        g.moveTo(cx - 17, y + 0.9);
        g.lineTo(cx + 17, y + 0.9);
        g.stroke();
        const vx = cx - 12 + rand() * 22;
        g.strokeStyle = 'rgba(0,0,8,0.35)';
        g.beginPath();
        g.moveTo(vx, y);
        g.lineTo(vx, y + 8);
        g.stroke();
      }
      g.fillStyle = 'rgba(255,150,60,0.85)'; // the warm point
      g.fillRect(cx - 0.8 + (rand() - 0.5) * 10, cy - 3 + rand() * 4, 1.6, 4);
      break;
    }
  }
  g.restore();

  // Directional light on the rim: NW edges catch it, SE edges fall away.
  const edge = (i1: number, style: string, w: number) => {
    const a1 = Math.PI / 6 + (Math.PI / 3) * i1;
    const a2 = Math.PI / 6 + (Math.PI / 3) * ((i1 + 1) % 6);
    g.beginPath();
    g.moveTo(cx + (HEX_R - 1) * Math.cos(a1), cy + (HEX_R - 1) * Math.sin(a1));
    g.lineTo(cx + (HEX_R - 1) * Math.cos(a2), cy + (HEX_R - 1) * Math.sin(a2));
    g.strokeStyle = style;
    g.lineWidth = w;
    g.stroke();
  };
  // vertices sit at 30°,90°,150°,210°,270°,330° (y-down): i=3→210°..270° is the
  // upper-left edge, i=2 the left; i=0 lower-right, i=5 the right.
  edge(3, 'rgba(255,255,255,0.16)', 1.4);
  edge(2, 'rgba(255,255,255,0.10)', 1.2);
  edge(0, 'rgba(0,0,14,0.30)', 1.6);
  edge(5, 'rgba(0,0,14,0.20)', 1.3);

  // Raised silhouettes — drawn UNclipped so they rise into the headroom.
  if (type === 'park') {
    const n = 2 + (variant % 2);
    for (let i = 0; i < n; i++) {
      const x = cx - 10 + (i * 20) / Math.max(1, n - 1) + (rand() - 0.5) * 6;
      const baseY = cy + 4 + rand() * 8;
      const h = 15 + rand() * 9;
      const w = 5.5 + rand() * 2.5;
      g.fillStyle = 'rgba(6,10,14,0.35)'; // cast shadow on the snow
      g.beginPath();
      g.ellipse(x + 2.5, baseY + 1, w * 0.9, 2, 0, 0, Math.PI * 2);
      g.fill();
      for (let t = 0; t < 3; t++) { // three stacked skirts
        const ty = baseY - (h * t) / 3.2;
        const tw = w * (1 - t * 0.26);
        const th = h * 0.46;
        g.beginPath();
        g.moveTo(x, ty - th);
        g.lineTo(x + tw, ty);
        g.lineTo(x - tw, ty);
        g.closePath();
        g.fillStyle = t % 2 ? '#24443a' : '#1d3a31';
        g.fill();
        g.beginPath(); // snow on the wind side — a dusting, not a slash
        g.moveTo(x, ty - th);
        g.lineTo(x - tw, ty);
        g.strokeStyle = 'rgba(225,240,245,0.38)';
        g.lineWidth = 0.85;
        g.stroke();
      }
    }
  }
  if (type === 'highrise') {
    const peaks = 2;
    for (let i = 0; i < peaks; i++) {
      const px = cx - 8 + i * 15 + (rand() - 0.5) * 5;
      const apexY = cy - HEX_R - 4 - rand() * 8;
      const baseY = cy + 10 + rand() * 6;
      const half = 11 + rand() * 4;
      g.beginPath(); // lit NW face
      g.moveTo(px, apexY);
      g.lineTo(px - half, baseY);
      g.lineTo(px + half * 0.3, baseY);
      g.closePath();
      g.fillStyle = '#48545f';
      g.fill();
      g.beginPath(); // shaded SE face
      g.moveTo(px, apexY);
      g.lineTo(px + half * 0.3, baseY);
      g.lineTo(px + half, baseY);
      g.closePath();
      g.fillStyle = '#242c38';
      g.fill();
      g.beginPath(); // snow cap
      g.moveTo(px, apexY);
      g.lineTo(px - half * 0.34, apexY + (baseY - apexY) * 0.3);
      g.lineTo(px + half * 0.12, apexY + (baseY - apexY) * 0.34);
      g.closePath();
      g.fillStyle = 'rgba(232,244,252,0.85)';
      g.fill();
    }
  }
  return c;
}

function stampFor(type: string, col: number, row: number): HTMLCanvasElement | null {
  if (!tileStamps) {
    tileStamps = {};
    for (const t of Object.keys(TILE_COLOR)) tileStamps[t] = [0, 1, 2, 3].map((v) => makeStamp(t, v));
  }
  const set = tileStamps[type];
  return set ? set[tileHash(col, row) & 3] : null;
}

// ── Map legend — the popup that answers "what am I looking at". Terrain order
// is a narrative ramp: common/safe ground first, then the costlier/rougher
// tiles, then the two special cases (stronghold, bridge), then the two
// impassables. Swatches reuse the SAME pre-rendered stamp art the board
// itself draws (see `stampFor` above) — not a flat colour chip.
const LEGEND_TERRAIN = [
  'block', 'street', 'rubble', 'park', 'slums', 'warzone', 'stronghold', 'bridge', 'river', 'highrise',
] as const;
const legendSwatchCache: Record<string, string> = {};
/** A cached PNG data-URL snapshot of this terrain's stamp art, for the legend list. */
function terrainSwatch(type: string): string {
  if (!legendSwatchCache[type]) {
    const c = stampFor(type, 0, 0);
    legendSwatchCache[type] = c ? c.toDataURL() : '';
  }
  return legendSwatchCache[type];
}
function isPassableType(type: string): boolean {
  return !IMPASSABLE.has(type);
}

/** Tier ladder for the legend's "enemies get stronger" section. Mirrors
 *  backend/src/game/map/hex-map-generator.ts (UNLOCK_LEVEL, TIER_MOB_POOL) —
 *  this table is frontend-only descriptive copy over backend-authoritative
 *  numbers; update BOTH sides if the tier count or unlock levels ever change. */
const LEGEND_TIERS: { tier: number; unlockLevel: number }[] = [
  { tier: 1, unlockLevel: 1 }, { tier: 2, unlockLevel: 4 },
  { tier: 3, unlockLevel: 7 }, { tier: 4, unlockLevel: 11 },
  { tier: 5, unlockLevel: 15 }, { tier: 6, unlockLevel: 20 },
  { tier: 7, unlockLevel: 26 }, { tier: 8, unlockLevel: 33 },
];
/** "On the map" section — the other things a new player sees and needs named. */
const LEGEND_OTHER = ['you', 'fog', 'turf', 'path'] as const;

// ── Cloud shadows — two soft shades drifting over the hold; the cheapest way
// to make a top-down board read as land under a sky instead of a spreadsheet.
const CLOUDS = [
  { x0: 300, y0: 500, r: 420, vx: 7.5, vy: 2.6, a: 0.14 },
  { x0: 1400, y0: 1400, r: 560, vx: -5.2, vy: 3.4, a: 0.11 },
];

// ── State ──
const canvasRef = ref<HTMLCanvasElement | null>(null);
const minimapRef = ref<HTMLCanvasElement | null>(null);
const loading = ref(true);
const error = ref('');
const nerve = ref<Nerve>({ current: 0, max: 0 });
/** Countdown to the next nerve pip — anchors ride along on every position/move payload. */
const nerveClock = useRegenClock(() =>
  nerve.value.max > 0 ? { anchor: nerve.value.regen, max: nerve.value.max } : null);
const posInfo = ref({ col: 0, row: 0 });
const hoverName = ref<string>('');       // district or mob label for the info bar
const hoverCost = ref<number | null>(null);
const hoverBlocked = ref(false);
const fighting = ref(false);
const showHelp = ref(false);             // "how to run the city" card toggle
const showLegend = ref(false);           // "what am I looking at" legend modal
const tacticalTarget = ref<{ col: number; row: number } | null>(null);
const garrisonTarget = ref<string | null>(null); // districtId for a turf takeover
const defendTarget = ref<string | null>(null);   // districtId whose siege we're breaking
const busyFight = ref(false);                    // start-* in flight (guards double-open)
const fightInitial = ref<FightView | null>(null); // the bout we started, handed to FightScreen

let map: MapData | null = null;
let COLS = 64;
let ROWS = 74;

// ── THE FRONTIER — ground being charted beyond the hold ─────────────────────
// QA 86cb8ztau: the map stopped dead at the grid's edge, so the world read as
// finished. A band of uncharted sectors now rings the hold — a bare hex lattice
// under heavy fog, with a quiet word that it is coming. Decorative only: every
// pointer path already bounds-checks against COLS/ROWS, so these hexes cannot
// be hovered, walked or fought over. The legend has promised this since 15.08
// ("Beyond the ice"); now the map itself says it.
const FRONTIER_RINGS = 8;

const camera = { x: 0, y: 0, scale: 1.0 };
let centered = false;
const drag = { active: false, sx: 0, sy: 0, cx: 0, cy: 0, moved: false };
let hovered: [number, number] | null = null;
let hovPath: [number, number][] = [];
const pos = { col: 0, row: 0 };
// Fog of war — cells the hero has ever seen; within SIGHT of the hero = full.
const SIGHT = 5;
let exploredSet = new Set<string>();
// Mafia turf partition: every hex → index into `districts` (client voronoi).
let districts: DistrictInfo[] = [];
let turfIdx: Uint8Array = new Uint8Array(0); // row * COLS + col → district index
let labels: { district: DistrictInfo; x: number; y: number }[] = [];
let animTime = 0;
let rafId = 0;

// Sentinel turfIdx value meaning "this hex's district isn't in our (possibly
// partial) summary" — always out of range since there are only 8 districts.
const NO_TURF = 255;

function turfOf(col: number, row: number): DistrictInfo | undefined {
  const idx = turfIdx[row * COLS + col];
  return idx < districts.length ? districts[idx] : undefined;
}

/** Human-readable "where the hero is standing" — the turf name, or the no-man's
 *  streets between turfs. Drives the HUD location plate (replaces raw col/row). */
const locationName = computed(() => {
  void posInfo.value; // re-evaluate whenever the hero moves
  return turfOf(posInfo.value.col, posInfo.value.row)?.name ?? t('city.openIce');
});

/** The district whose STRONGHOLD (objective hex) sits on (col,row), if any.
 *  Only matches districts present in the (possibly partial — seen-only)
 *  summary; an un-scouted stronghold falls through to a plain walk. */
function strongholdAt(col: number, row: number): DistrictInfo | undefined {
  return districts.find((d) => d.col === col && d.row === row);
}

/** (Re)build the turf partition + labels from a map payload. Labels sit on each
 *  district's stronghold — the objective — not a floating centroid.
 *  `m.districts` may now be a PARTIAL summary (seen districts only) — tiles
 *  whose districtId isn't in that summary get the NO_TURF sentinel rather
 *  than silently aliasing to district 0. */
function hydrateMap(m: MapData) {
  districts = m.districts ?? [];
  const idxOf = new Map(districts.map((d, i) => [d.id, i]));
  turfIdx = new Uint8Array(m.cols * m.rows);
  for (let r = 0; r < m.rows; r++) {
    for (let c = 0; c < m.cols; c++) {
      const id = m.tiles[r][c].districtId;
      turfIdx[r * m.cols + c] = id !== undefined ? idxOf.get(id) ?? NO_TURF : NO_TURF;
    }
  }
  labels = districts.map((district) => {
    const [x, y] = hexCenter(district.col, district.row);
    return { district, x, y };
  });
}

// ── Territory-entry notices ────────────────────────────────────────────────
// First time the hero sets foot on a family's turf we serve a calling card:
// district, family, tier — and an honest read on whether he's outgunned.
// "Seen" turfs persist per hero in localStorage; a deadly turf re-warns once
// per session even after it's been seen.
const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];
type TurfDanger = 'safe' | 'risky' | 'deadly';
const turfNotice = ref<{ d: DistrictInfo; danger: TurfDanger; crest?: string } | null>(null);
let turfNoticeTimer = 0;

/** Taking a ward said nothing — the banner just changed colour on the next
 *  redraw (QA 86cb1qvpt). This is the "the ground is yours" card, and it points
 *  at the wards, which is where the ground actually pays. */
const takenNotice = ref<{ d: DistrictInfo; crest?: string } | null>(null);
let lastTurfId: string | null | undefined; // undefined until the first check
const seenKey = () => `fimbulwake.ground-seen.${props.heroId ?? 'anon'}`;
let seenTurfs = new Set<string>();
try { seenTurfs = new Set<string>(JSON.parse(localStorage.getItem(seenKey()) ?? '[]')); } catch { /* fresh ledger */ }
const warnedThisSession = new Set<string>();

/** Shared by district turf notices AND the legend's tier ladder — same read,
 *  same thresholds, one place that decides "is this above your weight". */
function dangerForUnlock(unlockLevel: number): TurfDanger {
  const lvl = props.heroLevel;
  if (lvl == null) return 'safe';
  if (lvl < unlockLevel - 2) return 'deadly';
  if (lvl < unlockLevel) return 'risky';
  return 'safe';
}
function dangerOf(d: DistrictInfo): TurfDanger {
  return dangerForUnlock(d.unlockLevel);
}

/**
 * Announce a turf crossing — ONLY ever called after an actual move.
 *
 * `prime` records where the hero already is WITHOUT announcing anything. The
 * map (re)loads with `lastTurfId` unset, so the first check after a load looks
 * exactly like a transition; without priming, merely opening the City Map
 * announced "You've crossed into <turf>" for the turf the hero was already
 * standing on. Same for a refresh — neither is a crossing.
 */
function checkTurfEntry(prime = false) {
  const t = turfOf(pos.col, pos.row);
  const id = t?.id ?? null;
  if (id === lastTurfId) return;
  lastTurfId = id;
  if (prime || !t) return;
  const danger = dangerOf(t);
  const isNew = !seenTurfs.has(t.id);
  const reWarn = danger === 'deadly' && !warnedThisSession.has(t.id);
  if (!isNew && !reWarn) return;
  if (isNew) {
    seenTurfs.add(t.id);
    try { localStorage.setItem(seenKey(), JSON.stringify([...seenTurfs])); } catch { /* private mode */ }
  }
  if (danger !== 'safe') warnedThisSession.add(t.id);
  turfNotice.value = { d: t, danger, crest: crestArt(t.family) };
  window.clearTimeout(turfNoticeTimer);
  turfNoticeTimer = window.setTimeout(() => { turfNotice.value = null; }, 8000);
}

/** The district summary is fog-gated server-side (names ship once any cell of
 *  the turf is explored) but was only fetched on mount — so walking INTO fresh
 *  territory revealed nameless, untinted hexes (and a bare stronghold) until a
 *  reload. When newly explored tiles reference a district we don't know yet,
 *  refetch the map so the name, tint, label and calling card arrive live. */
async function maybeRefreshDistricts() {
  if (!map) return;
  const known = new Set(districts.map((d) => d.id));
  let unknown = false;
  for (const cell of exploredSet) {
    const i = cell.indexOf(',');
    const id = map.tiles[+cell.slice(i + 1)]?.[+cell.slice(0, i)]?.districtId;
    if (id && !known.has(id)) { unknown = true; break; }
  }
  if (!unknown) return;
  try {
    map = await fetchMap();
    hydrateMap(map);
  } catch { /* keep the stale summary — next move retries */ }
}

// Fog state of a hex: 'full' (in sight now), 'dim' (explored, out of sight), 'fog' (never seen).
function fogOf(col: number, row: number): 'full' | 'dim' | 'fog' {
  if (hexDist(pos.col, pos.row, col, row) <= SIGHT) return 'full';
  return exploredSet.has(`${col},${row}`) ? 'dim' : 'fog';
}

// ── Hex math ──
function hexCenter(col: number, row: number): [number, number] {
  return [col * HEX_W + ((row & 1) ? HEX_W / 2 : 0), row * HEX_R * 1.5];
}
function hexPath(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number) {
  ctx.beginPath();
  for (let i = 0; i < 6; i++) {
    const a = Math.PI / 6 + (Math.PI / 3) * i;
    const x = cx + r * Math.cos(a), y = cy + r * Math.sin(a);
    i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
  }
  ctx.closePath();
}
function pixelToHex(wx: number, wy: number): [number, number] {
  const rowEst = wy / (HEX_R * 1.5);
  let bestC = 0, bestR = 0, bestD = Infinity;
  for (let dr = -1; dr <= 1; dr++) {
    const r = Math.round(rowEst) + dr;
    if (r < 0 || r >= ROWS) continue;
    const offset = (r & 1) ? HEX_W / 2 : 0;
    const cEst = (wx - offset) / HEX_W;
    for (let dc = -1; dc <= 1; dc++) {
      const c = Math.round(cEst) + dc;
      if (c < 0 || c >= COLS) continue;
      const [hx, hy] = hexCenter(c, r);
      const d = (wx - hx) ** 2 + (wy - hy) ** 2;
      if (d < bestD) { bestD = d; bestC = c; bestR = r; }
    }
  }
  return [bestC, bestR];
}
function hexDist(c1: number, r1: number, c2: number, r2: number): number {
  const q1 = c1 - Math.floor((r1 - (r1 & 1)) / 2), s1 = r1;
  const q2 = c2 - Math.floor((r2 - (r2 & 1)) / 2), s2 = r2;
  const dq = q1 - q2, ds = s1 - s2;
  return Math.max(Math.abs(dq), Math.abs(ds), Math.abs(dq + ds));
}
function hexNeighborOffsets(row: number): [number, number][] {
  return (row & 1)
    ? [[1, 0], [-1, 0], [1, -1], [0, -1], [1, 1], [0, 1]]
    : [[1, 0], [-1, 0], [0, -1], [-1, -1], [0, 1], [-1, 1]];
}
// Client-side A* path preview (matches dominion — cosmetic + nerve estimate)
function clientAstar(sc: number, sr: number, ec: number, er: number): [number, number][] {
  if (!map) return [];
  const tiles = map.tiles;
  type N = { c: number; r: number; g: number; f: number; prev: N | null };
  const key = (c: number, r: number) => c * 1000 + r;
  const open = new Map<number, N>();
  const closed = new Set<number>();
  open.set(key(sc, sr), { c: sc, r: sr, g: 0, f: hexDist(sc, sr, ec, er), prev: null });
  for (let i = 0; i < 500 && open.size > 0; i++) {
    let cur: N | null = null;
    for (const n of open.values()) if (!cur || n.f < cur.f) cur = n;
    if (!cur) break;
    if (cur.c === ec && cur.r === er) {
      const path: [number, number][] = [];
      let n: N | null = cur;
      while (n) { path.unshift([n.c, n.r]); n = n.prev; }
      return path;
    }
    open.delete(key(cur.c, cur.r));
    closed.add(key(cur.c, cur.r));
    for (const [dc, dr] of hexNeighborOffsets(cur.r)) {
      const nc = cur.c + dc, nr = cur.r + dr;
      if (nc < 0 || nc >= COLS || nr < 0 || nr >= ROWS) continue;
      if (closed.has(key(nc, nr))) continue;
      const cost = TILE_COST[tiles[nr]?.[nc]?.type ?? 'block'] ?? 1;
      if (!isFinite(cost)) continue;
      // An enemy's hex is a wall (owner 02.09, CU-12418agekkx item 5) — same
      // rule as the server's findPath: never entered, never crossed.
      if (tiles[nr]?.[nc]?.mob) continue;
      const g = cur.g + cost;
      if (g > 10) continue;
      const ex = open.get(key(nc, nr));
      if (!ex || g < ex.g)
        open.set(key(nc, nr), { c: nc, r: nr, g, f: g + hexDist(nc, nr, ec, er), prev: cur });
    }
  }
  return [];
}

/** Summed entry cost of a path (start hex free) — the same number the server charges. */
function pathCost(p: [number, number][]): number {
  return p.slice(1).reduce((s, [pc, pr]) => s + (TILE_COST[map!.tiles[pr]?.[pc]?.type ?? 'block'] ?? 1), 0);
}

/**
 * Route TOWARD a hex the hero may not be allowed to enter. A hex with a live
 * enemy is a wall (owner 02.09, CU-12418agekkx item 5): you used to be able to
 * walk onto it, open the fight, cancel and keep walking — the mob was scenery.
 * Now the path ends on the cheapest FREE hex next to the enemy, and the fight
 * is picked from there; a cancelled fight leaves the hero exactly where they
 * stand, and the only way past is around, paying the detour. An empty hex
 * routes as before. Already adjacent → a one-hex path (nothing to walk).
 * Empty when no hex beside the enemy is reachable.
 */
function routeToward(ec: number, er: number): [number, number][] {
  if (!map) return [];
  if (!map.tiles[er]?.[ec]?.mob) return clientAstar(pos.col, pos.row, ec, er);
  if (hexDist(pos.col, pos.row, ec, er) <= 1) return [[pos.col, pos.row]];
  let best: [number, number][] = [];
  let bestCost = Infinity;
  for (const [dc, dr] of hexNeighborOffsets(er)) {
    const nc = ec + dc, nr = er + dr;
    if (nc < 0 || nc >= COLS || nr < 0 || nr >= ROWS) continue;
    const t = map.tiles[nr]?.[nc];
    if (!t || IMPASSABLE.has(t.type) || t.mob) continue;
    const p = clientAstar(pos.col, pos.row, nc, nr);
    if (p.length < 2) continue;
    const cost = pathCost(p);
    if (cost < bestCost) { bestCost = cost; best = p; }
  }
  return best;
}

// ── Draw ──
function draw() {
  const canvas = canvasRef.value;
  if (!canvas || !map) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const dpr = window.devicePixelRatio || 1;
  const W = canvas.clientWidth, H = canvas.clientHeight;
  if (canvas.width !== Math.round(W * dpr) || canvas.height !== Math.round(H * dpr)) {
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
  }
  if (W === 0 || H === 0) return;

  const { x: panX, y: panY, scale } = camera;
  const tiles = map.tiles;

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.fillStyle = '#0a1220';
  ctx.fillRect(0, 0, W, H);

  ctx.save();
  ctx.translate(W / 2 + panX, H / 2 + panY);
  ctx.scale(scale, scale);

  // Viewport-culled tile bounds
  const wl = (-W / 2 - panX) / scale, wt = (-H / 2 - panY) / scale;
  const wr = (W / 2 - panX) / scale, wb = (H / 2 - panY) / scale;
  const c0 = Math.max(0, Math.floor(wl / HEX_W) - 2);
  const c1 = Math.min(COLS - 1, Math.ceil(wr / HEX_W) + 2);
  const r0 = Math.max(0, Math.floor(wt / (HEX_R * 1.5)) - 2);
  const r1 = Math.min(ROWS - 1, Math.ceil(wb / (HEX_R * 1.5)) + 2);

  const pathSet = new Set<string>();
  for (const [pc, pr] of hovPath) pathSet.add(`${pc},${pr}`);
  const hov = hovered;

  for (let row = r0; row <= r1; row++) {
    for (let col = c0; col <= c1; col++) {
      const [cx, cy] = hexCenter(col, row);
      const tile = tiles[row]?.[col];
      const type = tile?.type ?? 'block';
      const turf = turfOf(col, row);

      // The relief pass: a pre-rendered lit stamp, not a flat swatch. Raised
      // stamps (spruce, crags) rise into the row above; the top-to-bottom row
      // order makes that painter-correct. Never-seen hexes get the FLAT fill —
      // their fog cover is hex-shaped, and a detailed stamp would poke its
      // treetops out from under it into the visible hex above.
      const fog = fogOf(col, row);
      const stamp = fog === 'fog' ? null : stampFor(type, col, row);
      if (stamp) {
        ctx.drawImage(stamp, cx - STAMP_W / 2, cy - HEX_R - STAMP_HEAD, STAMP_W, STAMP_H);
      } else {
        ctx.fillStyle = TILE_COLOR[type] ?? '#4f4a42';
        hexPath(ctx, cx, cy, HEX_R - 0.5);
        ctx.fill();
      }

      // Live ice: a slow glint sweeping along the frozen river.
      if (type === 'river' && fog === 'full') {
        const glint = 0.05 + 0.05 * Math.sin(animTime / 900 + (cx + cy) * 0.045);
        hexPath(ctx, cx, cy, HEX_R - 0.5);
        ctx.fillStyle = `rgba(226,244,255,${glint.toFixed(3)})`;
        ctx.fill();
      }

      // Mafia turf tint — the whole city is carved into family territories
      if (turf) {
        hexPath(ctx, cx, cy, HEX_R - 0.5);
        ctx.fillStyle = accentRgba(districtAccent(turf.id), 0.11);
        ctx.fill();
      }

      if (pathSet.has(`${col},${row}`)) {
        hexPath(ctx, cx, cy, HEX_R - 0.5);
        ctx.fillStyle = 'rgba(251,191,36,0.32)';
        ctx.fill();
      }
      if (hov && hov[0] === col && hov[1] === row) {
        hexPath(ctx, cx, cy, HEX_R - 0.5);
        ctx.fillStyle = 'rgba(255,255,255,0.15)';
        ctx.fill();
      }

      hexPath(ctx, cx, cy, HEX_R - 0.5);
      ctx.strokeStyle = TILE_BORDER[type] ?? '#403c35';
      ctx.lineWidth = scale < 0.6 ? 0.3 : 0.7;
      ctx.stroke();

      // Turf boundary — thick ink line on edges facing another family's turf
      if (turf) {
        const myIdx = turfIdx[row * COLS + col];
        for (const [dc, dr] of hexNeighborOffsets(row)) {
          const nc = col + dc, nr = row + dr;
          if (nc < 0 || nc >= COLS || nr < 0 || nr >= ROWS) continue;
          if (turfIdx[nr * COLS + nc] === myIdx) continue;
          // shared edge = the two hex corners nearest the neighbor's center
          const [nx, ny] = hexCenter(nc, nr);
          let a1 = -1, a2 = -1, d1 = Infinity, d2 = Infinity;
          for (let i = 0; i < 6; i++) {
            const ang = Math.PI / 6 + (Math.PI / 3) * i;
            const px = cx + (HEX_R - 0.5) * Math.cos(ang);
            const py = cy + (HEX_R - 0.5) * Math.sin(ang);
            const d = (px - nx) ** 2 + (py - ny) ** 2;
            if (d < d1) { d2 = d1; a2 = a1; d1 = d; a1 = i; }
            else if (d < d2) { d2 = d; a2 = i; }
          }
          const ang1 = Math.PI / 6 + (Math.PI / 3) * a1;
          const ang2 = Math.PI / 6 + (Math.PI / 3) * a2;
          ctx.beginPath();
          ctx.moveTo(cx + (HEX_R - 0.5) * Math.cos(ang1), cy + (HEX_R - 0.5) * Math.sin(ang1));
          ctx.lineTo(cx + (HEX_R - 0.5) * Math.cos(ang2), cy + (HEX_R - 0.5) * Math.sin(ang2));
          ctx.strokeStyle = 'rgba(8,6,14,0.85)';
          ctx.lineWidth = 3;
          ctx.stroke();
        }
      }

      // Family unit marker — only within sight; bob gently; disc in family accent.
      if (tile?.mob && HEX_R * scale >= 10 && fog === 'full') {
        const isMythic = tile.mob.kind === 'mythic';
        // A hold's elite beast — bigger than the local mobs, ringed in glacier
        // ice so it reads as "special" without borrowing the mythics' gold.
        const isElite = tile.mob.kind === 'elite';
        const lvl = Math.min(tile.mob.level, 15);
        const rad = (isMythic ? HEX_R * 0.46 : isElite ? HEX_R * 0.4 : HEX_R * 0.34) + (lvl / 15) * HEX_R * 0.2;
        const bob = Math.sin(animTime / 700 + col * 1.3 + row * 0.9) * (isMythic ? 3 : 2);
        const accent = districtAccent(tile.districtId ?? turf?.id);
        // The elite GLOWS (owner 01.09: "он как-то подсвечивается?") — a
        // breathing glacier-ice halo, the same beacon grammar as the player's
        // red and the mythics' gold, so the hold's beast is findable at a
        // glance without stealing either of those colours.
        if (isElite) {
          const eb = 1 + 0.08 * Math.sin(animTime / 520 + col + row);
          const halo = ctx.createRadialGradient(cx, cy + bob, rad * 0.6, cx, cy + bob, rad * 2 * eb);
          halo.addColorStop(0, 'rgba(159, 224, 255, 0.34)');
          halo.addColorStop(1, 'rgba(159, 224, 255, 0)');
          ctx.fillStyle = halo;
          ctx.beginPath(); ctx.arc(cx, cy + bob, rad * 2 * eb, 0, Math.PI * 2); ctx.fill();
          // a soft halo sinks into white snowfields — the pulsing ice ring is
          // the stroke that reads on any ground (same grammar as the player's
          // red pulse), seated on a dark hairline for the bright tiles.
          ctx.strokeStyle = 'rgba(6, 5, 10, 0.55)';
          ctx.lineWidth = 3.5;
          ctx.beginPath(); ctx.arc(cx, cy + bob, rad + 4 * eb, 0, Math.PI * 2); ctx.stroke();
          ctx.strokeStyle = 'rgba(159, 224, 255, 0.75)';
          ctx.lineWidth = 1.8;
          ctx.beginPath(); ctx.arc(cx, cy + bob, rad + 4 * eb, 0, Math.PI * 2); ctx.stroke();
        }
        // Mythics carry their own portraits (mobs/<type>.webp); kingpin is the
        // safety net for an id with no file.
        const portrait = isMythic
          ? (mobImg(tile.mob.type) ?? mobImg('kingpin'))
          : mobImg(tile.mob.type, tile.mob.archetype);
        if (portrait) {
          // Enemy head-shot in a slim family-accent bezel (gold for a mythic,
          // glacier ice for the hold's elite beast). Creature art gets the
          // wide window — the humanoid headshot crop beheads a beast.
          drawPortraitDisc(ctx, portrait, cx, cy + bob, rad, isMythic || isElite);
          if (isMythic) drawMythicRing(ctx, cx, cy + bob, rad, animTime);
          else if (isElite) {
            const ice = ctx.createLinearGradient(cx, cy + bob - rad, cx, cy + bob + rad);
            ice.addColorStop(0, '#dff5ff');
            ice.addColorStop(0.55, '#8fd0ee');
            ice.addColorStop(1, '#3f7fa8');
            drawDecoRing(ctx, cx, cy + bob, rad, ice);
          } else drawDecoRing(ctx, cx, cy + bob, rad, accent);
        } else {
          // Fallback: family-accent disc (art not yet loaded / unknown archetype).
          ctx.beginPath();
          ctx.arc(cx, cy + bob, rad, 0, Math.PI * 2);
          ctx.fillStyle = accent;
          ctx.fill();
          ctx.lineWidth = 1.4;
          ctx.strokeStyle = 'rgba(8,8,12,0.85)';
          ctx.stroke();
        }
        // No name on the board (owner 01.09: the labels stuck out) — the gold
        // ring already says "mythic", and the calling card names them on tap.
      }

    }
  }

  // Cloud shadows drift over the land — under the fog, over the terrain.
  {
    const mapW = COLS * HEX_W, mapH = ROWS * HEX_R * 1.5;
    const t = animTime / 1000;
    for (const cl of CLOUDS) {
      const spanX = mapW + cl.r * 2, spanY = mapH + cl.r * 2;
      const cxw = ((((cl.x0 + t * cl.vx) % spanX) + spanX) % spanX) - cl.r;
      const cyw = ((((cl.y0 + t * cl.vy) % spanY) + spanY) % spanY) - cl.r;
      const grad = ctx.createRadialGradient(cxw, cyw, cl.r * 0.25, cxw, cyw, cl.r);
      grad.addColorStop(0, `rgba(4,8,18,${cl.a})`);
      grad.addColorStop(1, 'rgba(4,8,18,0)');
      ctx.fillStyle = grad;
      ctx.fillRect(cxw - cl.r, cyw - cl.r, cl.r * 2, cl.r * 2);
    }
  }

  // Fog of war — a SECOND pass, after every stamp has landed, so a treetop
  // rising out of a lit hex can never paint over the fog of the hex above it.
  for (let row = r0; row <= r1; row++) {
    for (let col = c0; col <= c1; col++) {
      const fog = fogOf(col, row);
      if (fog === 'full') continue;
      const [cx, cy] = hexCenter(col, row);
      hexPath(ctx, cx, cy, HEX_R + 0.35); // slight overlap: no lit seams between fogged hexes
      ctx.fillStyle = fog === 'dim' ? 'rgba(0,0,10,0.58)' : 'rgba(0,0,8,0.92)';
      ctx.fill();
    }
  }

  // The frontier — uncharted sectors beyond the hold. Drawn after the fog so it
  // sits at the same depth as never-seen ground: a faint honeycomb, no relief,
  // no terrain, reading as map that has not been charted rather than as ground
  // you could walk to.
  {
    const fc0 = Math.max(-FRONTIER_RINGS, Math.floor(wl / HEX_W) - 2);
    const fc1 = Math.min(COLS - 1 + FRONTIER_RINGS, Math.ceil(wr / HEX_W) + 2);
    const fr0 = Math.max(-FRONTIER_RINGS, Math.floor(wt / (HEX_R * 1.5)) - 2);
    const fr1 = Math.min(ROWS - 1 + FRONTIER_RINGS, Math.ceil(wb / (HEX_R * 1.5)) + 2);
    ctx.lineWidth = Math.max(0.4, 0.6 / scale);
    for (let row = fr0; row <= fr1; row++) {
      for (let col = fc0; col <= fc1; col++) {
        if (col >= 0 && col < COLS && row >= 0 && row < ROWS) continue;
        const [cx, cy] = hexCenter(col, row);
        hexPath(ctx, cx, cy, HEX_R - 0.5);
        ctx.fillStyle = 'rgba(13,22,38,0.62)';
        ctx.fill();
        ctx.strokeStyle = 'rgba(140,170,210,0.055)';
        ctx.stroke();
      }
    }

    // A word in each band, so whichever edge the player drifts to says the same
    // thing. Kept to the map's own small-caps voice and deliberately dim.
    const mid = FRONTIER_RINGS / 2;
    const marks: [number, number][] = [
      [COLS / 2, -mid], [COLS / 2, ROWS - 1 + mid],
      [-mid, ROWS / 2], [COLS - 1 + mid, ROWS / 2],
    ];
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `${14 / Math.max(scale, 0.5)}px "Cormorant SC", Georgia, serif`;
    ctx.fillStyle = 'rgba(188,203,219,0.42)';
    for (const [mc, mr] of marks) {
      const [cx, cy] = hexCenter(mc, mr);
      if (cx < wl || cx > wr || cy < wt || cy > wb) continue;
      ctx.fillText(t('city.comingSoon'), cx, cy);
    }
  }

  // MYTHIC BEACONS — the four world bosses shine THROUGH the fog
  // (CU-86cbc65ny item 10). They are seeded at the frozen edges, which no
  // player had explored, so the whole feature read as "not added": the mob
  // loop above draws markers only in full sight. A legend is heard of before
  // it is seen — the mythic's disc, ring and name now render over the fog
  // cover (slightly dimmed under it), so the board always shows where the
  // monsters wait. In-sight mythics are already drawn lit by the loop above.
  for (let row = r0; row <= r1; row++) {
    for (let col = c0; col <= c1; col++) {
      const tile = tiles[row]?.[col];
      if (tile?.mob?.kind !== 'mythic' || fogOf(col, row) === 'full') continue;
      const [cx, cy] = hexCenter(col, row);
      const lvl = Math.min(tile.mob.level, 15);
      const rad = HEX_R * 0.46 + (lvl / 15) * HEX_R * 0.2;
      const bob = Math.sin(animTime / 700 + col * 1.3 + row * 0.9) * 3;
      // Marker only, no name (owner 01.09: the labels stuck out) — the gold
      // ring is the tell, the calling card names the boss on tap.
      ctx.save();
      ctx.globalAlpha = 0.88;
      const portrait = mobImg(tile.mob.type) ?? mobImg('kingpin');
      if (portrait) {
        drawPortraitDisc(ctx, portrait, cx, cy + bob, rad, true);
        drawMythicRing(ctx, cx, cy + bob, rad, animTime);
      }
      ctx.restore();
    }
  }

  // Mafia turf labels sit ON each stronghold (the objective): a ring marks the
  // exact capture hex, the crest + family + district + tier/lock read above it.
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const inv = 1 / Math.max(scale, 0.5);
  const fs = 19 * inv; // ~constant on-screen size
  for (const l of labels) {
    const accent = districtAccent(l.district.id);
    const besieged = l.district.owner === 'you' && l.district.underSiege;
    const objColor = besieged ? '#e0524a' : l.district.locked ? '#c07070' : l.district.owner === 'you' ? '#e6c15a' : accent;

    // Objective ring on the stronghold hex — pulses gently so it reads as a goal.
    // A besieged ward's ring beats twice as fast and burns red: come home NOW.
    const pulse = 1 + (besieged ? 0.14 : 0.08) * Math.sin(animTime / (besieged ? 250 : 500));
    ctx.save();
    ctx.beginPath();
    ctx.arc(l.x, l.y, HEX_R * 0.74 * pulse, 0, Math.PI * 2);
    ctx.lineWidth = (besieged ? 4 : 3) * inv;
    ctx.strokeStyle = accentRgba(objColor, besieged ? 1 : 0.9);
    ctx.setLineDash([7 * inv, 5 * inv]);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();

    // Family crest above the ring (round, accent border).
    const crest = crestImg(l.district.family);
    const cs = fs * 2.3;
    const ccy = l.y - HEX_R * 0.72 - cs * 0.5;
    if (crest) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(l.x, ccy, cs / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(crest, l.x - cs / 2, ccy - cs / 2, cs, cs);
      ctx.restore();
      drawDecoRing(ctx, l.x, ccy, cs / 2, objColor);
    }

    // Text block below the ring.
    const ty = l.y + HEX_R * 0.72 + fs * 0.7;
    ctx.lineWidth = 4 * inv;
    ctx.strokeStyle = 'rgba(6,6,10,0.95)';
    ctx.font = `bold ${fs}px "Cinzel", Georgia, serif`;
    ctx.strokeText(l.district.family.toUpperCase(), l.x, ty);
    ctx.fillStyle = accent;
    ctx.fillText(l.district.family.toUpperCase(), l.x, ty);

    ctx.font = `${fs * 0.7}px "Cinzel", Georgia, serif`;
    ctx.strokeText(l.district.name.toUpperCase(), l.x, ty + fs * 0.95);
    ctx.fillStyle = '#efe4c4';
    ctx.fillText(l.district.name.toUpperCase(), l.x, ty + fs * 0.95);

    const tag = l.district.owner === 'you'
      ? t('city.tierYourGround', { n: l.district.tier })
      : l.district.locked
        ? t('city.tierLocked', { n: l.district.tier, level: l.district.unlockLevel })
        : t('city.tierTakeIt', { n: l.district.tier });
    ctx.font = `bold ${fs * 0.54}px "Cormorant SC", "Courier New", monospace`;
    ctx.strokeText(tag, l.x, ty + fs * 1.72);
    ctx.fillStyle = l.district.owner === 'you' ? '#e6c15a' : l.district.locked ? '#d78a8a' : 'rgba(236,227,207,0.82)';
    ctx.fillText(tag, l.x, ty + fs * 1.72);
  }

  // Impassable indicator (red X on hovered highrise/river)
  if (hov) {
    const [hc, hr] = hov;
    const hovType = tiles[hr]?.[hc]?.type ?? 'block';
    if (IMPASSABLE.has(hovType)) {
      const [cx, cy] = hexCenter(hc, hr);
      const xr = HEX_R * 0.42;
      ctx.strokeStyle = 'rgba(239,68,68,0.9)';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(cx - xr, cy - xr); ctx.lineTo(cx + xr, cy + xr); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(cx + xr, cy - xr); ctx.lineTo(cx - xr, cy + xr); ctx.stroke();
    }
  }

  // Player marker — the hero's PORTRAIT in a blood-red bezel (owner 01.09:
  // the red dot stays on the minimap only; the world map shows your face,
  // ringed red). The red rim is what says "you" — no enemy disc carries it —
  // and it sits on a dark seat so it reads on red clan ground and white snow
  // alike. The breathing red halo + pulsing ring stay as the "find me" beacon.
  {
    const [cx, cy] = hexCenter(pos.col, pos.row);
    const r = Math.max(8, HEX_R * 0.5);
    const breathe = 1 + 0.06 * Math.sin(animTime / 600);
    ctx.save();
    // breathing red halo — the "find me" beacon
    const halo = ctx.createRadialGradient(cx, cy, r * 0.6, cx, cy, r * 2.1 * breathe);
    halo.addColorStop(0, 'rgba(225,29,46,0.34)');
    halo.addColorStop(1, 'rgba(225,29,46,0)');
    ctx.fillStyle = halo;
    ctx.beginPath(); ctx.arc(cx, cy, r * 2.1 * breathe, 0, Math.PI * 2); ctx.fill();
    // pulsing outer ring keeps the "you are here" read
    ctx.strokeStyle = 'rgba(255,90,100,0.5)';
    ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.arc(cx, cy, r + 5 * breathe, 0, Math.PI * 2); ctx.stroke();

    const me = classImg(props.heroClass);
    if (me) {
      drawPortraitDisc(ctx, me, cx, cy, r);
      // dark seat, then the red bezel over the portrait's edge — SLIM
      // (owner 01.09: the first cut's bezel was too fat)
      ctx.strokeStyle = 'rgba(6,5,10,0.75)';
      ctx.lineWidth = Math.max(2.5, r * 0.18);
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
      const rim = ctx.createLinearGradient(cx, cy - r, cx, cy + r);
      rim.addColorStop(0, '#ff5a64');
      rim.addColorStop(0.55, '#e11d2e');
      rim.addColorStop(1, '#8f0f1c');
      ctx.strokeStyle = rim;
      ctx.lineWidth = Math.max(1.6, r * 0.11);
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.stroke();
    } else {
      // portrait not loaded yet — the red bead stands in
      const bead = ctx.createRadialGradient(cx - r * 0.3, cy - r * 0.35, r * 0.15, cx, cy, r);
      bead.addColorStop(0, '#ff5a64');
      bead.addColorStop(0.55, '#e11d2e');
      bead.addColorStop(1, '#8f0f1c');
      ctx.fillStyle = bead;
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill();
    }
    ctx.restore();
  }

  ctx.restore();

  // Minimap
  const mm = minimapRef.value;
  if (mm) {
    const mc = mm.getContext('2d');
    if (mc) {
      const MM = 148;
      // The charted hold is inset so the frontier band shows around it — the
      // minimap used to fill edge to edge, which said the world stops here.
      const pad = Math.round((MM * FRONTIER_RINGS) / (COLS + FRONTIER_RINGS * 2));
      const span = MM - pad * 2;
      const tw = span / COLS, th = span / ROWS;
      mc.clearRect(0, 0, MM, MM);
      mc.fillStyle = 'rgba(13,22,38,0.62)';
      mc.fillRect(0, 0, MM, MM);
      for (let r = 0; r < ROWS; r++)
        for (let c = 0; c < COLS; c++) {
          mc.fillStyle = TILE_COLOR[tiles[r]?.[c]?.type ?? 'block'];
          mc.fillRect(pad + Math.floor(c * tw), pad + Math.floor(r * th), Math.ceil(tw) + 1, Math.ceil(th) + 1);
          const turf = turfOf(c, r);
          if (turf) {
            mc.fillStyle = accentRgba(districtAccent(turf.id), 0.28);
            mc.fillRect(pad + Math.floor(c * tw), pad + Math.floor(r * th), Math.ceil(tw) + 1, Math.ceil(th) + 1);
          }
        }
      // The four mythic beacons — gold dots, fog or no fog (item 10): the
      // minimap is where a player decides where to walk next, and a world
      // boss is a destination.
      mc.fillStyle = '#f0dda4';
      mc.strokeStyle = 'rgba(6,5,10,0.75)';
      mc.lineWidth = 1.5;
      for (let r = 0; r < ROWS; r++)
        for (let c = 0; c < COLS; c++) {
          if (tiles[r]?.[c]?.mob?.kind !== 'mythic') continue;
          mc.beginPath();
          mc.arc(pad + (c + 0.5) * tw, pad + (r + 0.5) * th, 2.6, 0, Math.PI * 2);
          mc.stroke(); mc.fill();
        }

      // The hero: same red-dot-in-a-bright-rim as the main board (item 7) —
      // the old bare gold dot vanished into gold-washed clan ground.
      const hx = pad + (pos.col + 0.5) * tw;
      const hy = pad + (pos.row + 0.5) * th;
      mc.strokeStyle = 'rgba(6,5,10,0.75)';
      mc.lineWidth = 3.5;
      mc.beginPath(); mc.arc(hx, hy, 3.5, 0, Math.PI * 2); mc.stroke();
      mc.strokeStyle = '#f5f9ff';
      mc.lineWidth = 2;
      mc.beginPath(); mc.arc(hx, hy, 3.5, 0, Math.PI * 2); mc.stroke();
      mc.fillStyle = '#e11d2e';
      mc.beginPath(); mc.arc(hx, hy, 3.5, 0, Math.PI * 2); mc.fill();
    }
  }
}

// ── Camera helpers ──
/**
 * The camera TRACKS the hero (QA 18.08 item 1): after every move, if the icon
 * has drifted out of the middle 60% of the view, glide it back to center.
 * Only edge drift triggers the glide, so a player panning around to scout
 * isn't yanked back while the icon is still comfortably on screen.
 */
function followHero() {
  const canvas = canvasRef.value;
  if (!canvas) return;
  const [hx, hy] = hexCenter(pos.col, pos.row);
  // world → screen: the inverse of screenToWorld()
  const sx = hx * camera.scale + camera.x + canvas.clientWidth / 2;
  const sy = hy * camera.scale + camera.y + canvas.clientHeight / 2;
  const mx = canvas.clientWidth * 0.2;
  const my = canvas.clientHeight * 0.2;
  if (sx < mx || sx > canvas.clientWidth - mx || sy < my || sy > canvas.clientHeight - my) {
    panCameraTo(pos.col, pos.row, 450);
  }
}

function centerOnHero() {
  cancelAnimationFrame(panRaf);
  const [hx, hy] = hexCenter(pos.col, pos.row);
  camera.x = -(hx * camera.scale);
  camera.y = -(hy * camera.scale);
  draw();
}

// ── Smooth camera pan (eased) to a hex, e.g. a district's stronghold ──────────
let panRaf = 0;
let focusPanned = false;
function panCameraTo(col: number, row: number, duration = 750) {
  cancelAnimationFrame(panRaf);
  const [tx, ty] = hexCenter(col, row);
  const targetX = -(tx * camera.scale);
  const targetY = -(ty * camera.scale);
  const startX = camera.x, startY = camera.y;
  const dx = targetX - startX, dy = targetY - startY;
  const reduce = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce || (Math.abs(dx) < 1 && Math.abs(dy) < 1)) {
    camera.x = targetX; camera.y = targetY; draw(); return;
  }
  const ease = (t: number) => 1 - Math.pow(1 - t, 3); // easeOutCubic
  let t0: number | null = null;
  const step = (now: number) => {
    if (t0 === null) t0 = now;
    const p = Math.min(1, (now - t0) / duration);
    const e = ease(p);
    camera.x = startX + dx * e;
    camera.y = startY + dy * e;
    draw();
    if (p < 1) panRaf = requestAnimationFrame(step);
  };
  panRaf = requestAnimationFrame(step);
}

/** One-shot: on "Take it on the map", start on the hero, then glide to the
 *  target district's stronghold. Consumed once (clears the parent's focus). */
function maybeFocusPan() {
  if (focusPanned || !props.focusDistrict) return;
  focusPanned = true;
  const d = districts.find((x) => x.id === props.focusDistrict);
  if (d) panCameraTo(d.col, d.row);
  emit('focused');
}

// ── Interaction ──
function updateHover(col: number, row: number) {
  if (!map) return;
  const prev = hovered;
  if (prev && prev[0] === col && prev[1] === row) return;
  hovered = [col, row];
  // Over an enemy the preview stops on the free hex beside them — that is
  // where the walk actually ends and what the fury quote must price.
  hovPath = routeToward(col, row);
  const tile = map.tiles[row]?.[col];
  const type = tile?.type ?? 'block';
  const impassable = IMPASSABLE.has(type);
  hoverBlocked.value = impassable;
  if (impassable) {
    hoverCost.value = null;
  } else if (hovPath.length > 1) {
    hoverCost.value = pathCost(hovPath);
  } else {
    hoverCost.value = null;
  }
  // Turf name for the hover readout: prefer the full summary (family + name)
  // when we have it; the tile itself always carries a plain districtName
  // (baked at map gen, independent of the possibly-partial seen-only summary),
  // so hover still resolves a name for turf we haven't scouted into `districts` yet.
  const turf = turfOf(col, row);
  hoverName.value = tile?.mob
    ? t('city.fight.mobInfo', { name: tile.mob.name, level: tile.mob.level, cash: tile.mob.cash })
    : turf
      ? t('city.fight.turfInfo', { family: turf.family, name: turf.name })
      : (tile?.districtName ?? '');
  draw();
}

function screenToWorld(clientX: number, clientY: number): [number, number] {
  const canvas = canvasRef.value!;
  const rect = canvas.getBoundingClientRect();
  const relX = clientX - rect.left, relY = clientY - rect.top;
  const W = canvas.clientWidth, H = canvas.clientHeight;
  return [
    (relX - W / 2 - camera.x) / camera.scale,
    (relY - H / 2 - camera.y) / camera.scale,
  ];
}

async function doMove(col: number, row: number) {
  try {
    const res = await moveTo(col, row);
    pos.col = res.col; pos.row = res.row;
    posInfo.value = { col: res.col, row: res.row };
    nerve.value = res.nerve;
    if (res.exploredCells) exploredSet = new Set(res.exploredCells);
    emit('nerve', res.nerve);
    hovPath = [];
    await maybeRefreshDistricts();
    checkTurfEntry();
    draw();
    followHero();
  } catch (err) {
    error.value = (err as Error).message;
  }
}

// ── Pre-fight calling card — every fight is confirmed before blood spills ──
const confirmFight = ref<
  | { kind: 'mob'; col: number; row: number; mob: MobData }
  | { kind: 'garrison'; d: DistrictInfo }
  | { kind: 'defend'; d: DistrictInfo }
  | null
>(null);

// Ghost-tap guard (QA, mobile): the tap that opens the card can land where the
// gold button appears — pointerup then fires "Start the fight" instantly. Arm
// the button only 500ms after the card opens so the opening tap can't spend it.
const confirmArmed = ref(false);
let confirmArmTimer = 0;
watch(confirmFight, (v) => {
  window.clearTimeout(confirmArmTimer);
  confirmArmed.value = false;
  if (v) confirmArmTimer = window.setTimeout(() => { confirmArmed.value = true; }, 500);
});

/** Honest odds read vs the hero's level (same tone as the turf notices). */
function fightOdds(lvl: number): { label: string; cls: string; note: string } {
  const diff = lvl - (props.heroLevel ?? 1);
  if (diff >= 4) return { label: t('city.danger.deadly'), cls: 'is-deadly', note: t('city.danger.deadlyNote') };
  if (diff >= 2) return { label: t('city.danger.risky'), cls: 'is-risky', note: t('city.danger.riskyNote') };
  if (diff <= -3) return { label: t('city.danger.easyPrey'), cls: 'is-safe', note: t('city.danger.easyNote') };
  return { label: t('city.danger.fairFight'), cls: 'is-safe', note: t('city.danger.fairNote') };
}
const confirmOdds = computed(() =>
  confirmFight.value?.kind === 'mob' ? fightOdds(confirmFight.value.mob.level) : null);
const heroHpPct = computed(() =>
  props.heroHp != null && props.heroMaxHp ? Math.round((props.heroHp / props.heroMaxHp) * 100) : null);

/**
 * Open the bout — but START it here, not inside the fight screen.
 *
 * Starting is what validates: nerve, hero HP, the level/tier gate and the
 * spatial gate all reject at start-*. Doing it here means "Not enough nerve"
 * lands on the map, where the player still is, instead of throwing them onto a
 * fight screen with nothing on it but an error.
 */
async function onConfirmFight() {
  const c = confirmFight.value;
  if (!c || busyFight.value) return;
  busyFight.value = true;
  error.value = '';
  try {
    const v = c.kind === 'mob'
      ? await startFight(c.col, c.row)
      : c.kind === 'defend'
        ? await startDefend(c.d.id)
        : await startGarrison(c.d.id);
    confirmFight.value = null;
    fightInitial.value = v;
    if (c.kind === 'mob') tacticalTarget.value = { col: c.col, row: c.row };
    else if (c.kind === 'defend') defendTarget.value = c.d.id;
    else garrisonTarget.value = c.d.id;
    // start-* already spent the nerve server-side — surface it now, not at bout
    // end, so the header chip and HUD gauge don't lie for the whole fight.
    void fetchPosition()
      .then((p) => { nerve.value = p.nerve; emit('nerve', p.nerve); })
      .catch(() => { /* cosmetic — reconciled on fight close anyway */ });
  } catch (err) {
    confirmFight.value = null;
    error.value = (err as Error).message; // e.g. "Not enough nerve (need 1)."
  } finally {
    busyFight.value = false;
  }
}

// Clicking an enemy serves the calling card; accepting it opens the interactive
// tactical fight for (col,row). The arena drives the fight turn-by-turn against
// the server; we just refresh on close.
function fight(col: number, row: number) {
  error.value = '';
  const mob = map?.tiles[row]?.[col]?.mob;
  if (!mob) return;
  confirmFight.value = { kind: 'mob', col, row, mob };
}

async function onTacticalDone(_changed: boolean) {
  tacticalTarget.value = null;
  // Refresh UNCONDITIONALLY: `changed=false` only means the bout didn't settle
  // (a flee) — but nerve was spent at start-fight and HP was lost in the rounds,
  // so "nothing changed" is never true once a fight opened. Gating on it left
  // the header chips and the HUD nerve gauge reading pre-fight values.
  try {
    const [m, p] = await Promise.all([fetchMap(), fetchPosition()]);
    map = m;
    pos.col = p.col; pos.row = p.row; posInfo.value = { col: p.col, row: p.row };
    nerve.value = p.nerve;
    exploredSet = new Set(p.exploredCells ?? []);
    checkTurfEntry(true); // a refresh re-seats us on the same tile — not a crossing
    draw();
    emit('nerve', p.nerve); emit('updated');
  } catch (err) {
    error.value = (err as Error).message;
  }
}

// ── Pointer / wheel handlers ──
function onPointerDown(e: PointerEvent) {
  if (e.button !== 0) return;
  cancelAnimationFrame(panRaf); // user grabs the map — stop any auto-pan in flight
  canvasRef.value?.setPointerCapture(e.pointerId);
  drag.active = true; drag.moved = false;
  drag.sx = e.clientX; drag.sy = e.clientY;
  drag.cx = camera.x; drag.cy = camera.y;
}
function onPointerMove(e: PointerEvent) {
  if (!drag.active) {
    const [wx, wy] = screenToWorld(e.clientX, e.clientY);
    const [col, row] = pixelToHex(wx, wy);
    if (col >= 0 && col < COLS && row >= 0 && row < ROWS) updateHover(col, row);
    return;
  }
  const dx = e.clientX - drag.sx, dy = e.clientY - drag.sy;
  if (!drag.moved && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) drag.moved = true;
  if (drag.moved) { camera.x = drag.cx + dx; camera.y = drag.cy + dy; draw(); }
}
async function onPointerUp(e: PointerEvent) {
  if (!drag.active) return;
  const wasDrag = drag.moved;
  drag.active = false; drag.moved = false;
  if (wasDrag) return;
  if (fighting.value || tacticalTarget.value || garrisonTarget.value || defendTarget.value || confirmFight.value || !map) return;
  const [wx, wy] = screenToWorld(e.clientX, e.clientY);
  const [col, row] = pixelToHex(wx, wy);
  if (col < 0 || col >= COLS || row < 0 || row >= ROWS) return;
  error.value = '';
  const tile = map.tiles[row]?.[col];

  if (tile?.mob) {
    // Spatial combat: if already adjacent, fight; else walk to the cheapest
    // free hex NEXT to the enemy (their own hex is a wall — owner 02.09, the
    // server refuses to enter it too), then fight from there. Cancelling the
    // fight leaves the hero on that hex; the enemy still bars the way.
    if (hexDist(pos.col, pos.row, col, row) <= 1) { await fight(col, row); return; }
    const path = routeToward(col, row);
    if (path.length < 2) { error.value = t('city.noRoute'); return; }
    const [ac, ar] = path[path.length - 1]; // the free hex beside the enemy
    await doMove(ac, ar);
    if (!error.value && hexDist(pos.col, pos.row, col, row) <= 1) await fight(col, row);
    return;
  }

  // Stronghold (turf objective): take the district with an interactive garrison
  // fight — but only by STANDING ON it. If you're not on it yet, walk there along
  // the route, then the takeover opens in the same click.
  const sh = strongholdAt(col, row);
  // Your own ward with raiders at the gate: same walk-then-fight contract, but
  // the bout is a DEFENSE — break the siege before the window runs out.
  if (sh && sh.owner === 'you' && sh.underSiege) {
    if (pos.col === col && pos.row === row) { confirmFight.value = { kind: 'defend', d: sh }; return; }
    await doMove(col, row);
    if (!error.value && pos.col === col && pos.row === row) confirmFight.value = { kind: 'defend', d: sh };
    return;
  }
  if (sh && sh.owner !== 'you') {
    if (sh.locked) {
      error.value = t('city.locked', { name: sh.name, level: sh.unlockLevel });
      return;
    }
    if (pos.col === col && pos.row === row) { confirmFight.value = { kind: 'garrison', d: sh }; return; }
    await doMove(col, row);
    if (!error.value && pos.col === col && pos.row === row) confirmFight.value = { kind: 'garrison', d: sh };
    return;
  }

  // Empty hex: ignore impassable clicks; else walk there (server A* over terrain).
  if (tile && IMPASSABLE.has(tile.type)) return;
  await doMove(col, row);
}

/** Turf takeover finished — refresh ownership + position from the server.
 *  Unconditional for the same reason as onTacticalDone: a fled garrison bout
 *  still spent nerve at start and HP in the rounds. */
async function onCaptureDone(_changed: boolean) {
  const targetId = garrisonTarget.value;
  // Ownership BEFORE the refresh — the only way to tell a conquest from a
  // bout we lost on ground we already held.
  const heldBefore = map?.districts.find((d) => d.id === targetId)?.owner === 'you';
  garrisonTarget.value = null;
  try {
    const [m, p] = await Promise.all([fetchMap(), fetchPosition()]);
    map = m; hydrateMap(m);
    pos.col = p.col; pos.row = p.row; posInfo.value = { col: p.col, row: p.row };
    nerve.value = p.nerve; exploredSet = new Set(p.exploredCells ?? []);
    emit('nerve', p.nerve); emit('updated');
    draw();
    const taken = m.districts.find((d) => d.id === targetId);
    if (taken && taken.owner === 'you' && !heldBefore) {
      turfNotice.value = null; // the two cards share the same slot
      takenNotice.value = { d: taken, crest: crestArt(taken.family) };
      sfx.sting(true);
    }
  } catch (e) { error.value = (e as Error).message; }
}
/** Siege defense finished — refresh ownership/siege state; celebrate a break. */
async function onDefendDone(_changed: boolean) {
  const targetId = defendTarget.value;
  defendTarget.value = null;
  try {
    const [m, p] = await Promise.all([fetchMap(), fetchPosition()]);
    map = m; hydrateMap(m);
    pos.col = p.col; pos.row = p.row; posInfo.value = { col: p.col, row: p.row };
    nerve.value = p.nerve; exploredSet = new Set(p.exploredCells ?? []);
    emit('nerve', p.nerve); emit('updated');
    draw();
    const d = m.districts.find((x) => x.id === targetId);
    if (d && d.owner === 'you' && !d.underSiege) {
      toast(t('city.siegeBroken', { name: d.name }), { icon: crestArt(d.family) });
      sfx.sting(true);
    }
  } catch (e) { error.value = (e as Error).message; }
}

/** One warning per map load: name every ward of yours with raiders at the gate. */
let siegeWarned = false;
function warnSieges() {
  if (siegeWarned || !map) return;
  const besieged = map.districts.filter((d) => d.owner === 'you' && d.underSiege);
  if (!besieged.length) return;
  siegeWarned = true;
  for (const d of besieged) {
    const left = d.siegeEndsAt ? Math.max(0, new Date(d.siegeEndsAt).getTime() - Date.now()) : 0;
    const hrs = Math.floor(left / 3_600_000), min = Math.floor((left % 3_600_000) / 60_000);
    toast(t('city.siegeWarning', { name: d.name, hrs, min }), { icon: crestArt(d.family) });
  }
}

function onPointerLeave() { hovered = null; hovPath = []; hoverName.value = ''; hoverCost.value = null; hoverBlocked.value = false; draw(); }
function onWheel(e: WheelEvent) {
  e.preventDefault();
  const canvas = canvasRef.value!;
  const rect = canvas.getBoundingClientRect();
  const W = canvas.clientWidth, H = canvas.clientHeight;
  const relX = e.clientX - rect.left, relY = e.clientY - rect.top;
  const wx0 = (relX - W / 2 - camera.x) / camera.scale;
  const wy0 = (relY - H / 2 - camera.y) / camera.scale;
  const factor = e.deltaY > 0 ? 0.94 : 1.06;
  const newScale = Math.min(3.5, Math.max(0.25, camera.scale * factor));
  camera.scale = newScale;
  camera.x = relX - W / 2 - wx0 * newScale;
  camera.y = relY - H / 2 - wy0 * newScale;
  draw();
}

let ro: ResizeObserver | null = null;

onMounted(async () => {
  try {
    const [m, p] = await Promise.all([fetchMap(), fetchPosition()]);
    map = m; COLS = m.cols; ROWS = m.rows;
    pos.col = p.col; pos.row = p.row;
    posInfo.value = { col: p.col, row: p.row };
    nerve.value = p.nerve;
    exploredSet = new Set(p.exploredCells ?? []);

    // Mafia turf partition comes baked onto every tile (districtId); labels sit
    // on each stronghold (the objective), matching the server's ownership.
    hydrateMap(m);
    checkTurfEntry(true); // opening the map is not a crossing — just record where we are
    warnSieges();         // raiders at any of your gates? say it NOW, with the clock

    const canvas = canvasRef.value!;
    ro = new ResizeObserver(() => {
      if (!centered && canvas.clientWidth >= 10) { centerOnHero(); centered = true; maybeFocusPan(); }
      draw();
    });
    ro.observe(canvas);
    if (canvas.clientWidth >= 10) { centerOnHero(); centered = true; maybeFocusPan(); }
    draw();

    // Throttled rAF loop for mob bob (~15 FPS)
    let last = 0;
    const loop = (t: number) => {
      if (t - last >= 66) { last = t; animTime = t; draw(); }
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
  } catch (err) {
    error.value = (err as Error).message;
  } finally {
    loading.value = false;
  }
});

onUnmounted(() => {
  if (rafId) cancelAnimationFrame(rafId);
  cancelAnimationFrame(panRaf);
  window.clearTimeout(confirmArmTimer);
  ro?.disconnect();
});
</script>

<template>
  <div class="relative w-full h-full bg-bg overflow-hidden">
    <canvas ref="canvasRef" class="block w-full h-full"
            style="cursor: crosshair; touch-action: none"
            @pointerdown="onPointerDown" @pointermove="onPointerMove"
            @pointerup="onPointerUp" @pointerleave="onPointerLeave" @wheel="onWheel"></canvas>

    <!-- the air over the hold: edge fog, aurora at the top of the sky, frost in
         the corners. pointer-events none — the canvas keeps every gesture. -->
    <div class="cm-atmo" aria-hidden="true"></div>

    <!-- territory-entry calling card -->
    <Transition name="turf-notice">
      <div v-if="turfNotice" class="cm-turf-notice bound-frame relative" :class="`is-${turfNotice.danger}`" role="status">
                        <img v-if="turfNotice.crest" :src="turfNotice.crest" class="cm-tn-crest" alt="" />
        <div class="cm-tn-body">
          <div class="cm-tn-kicker">{{ t('city.crossedInto') }}</div>
          <div class="cm-tn-name">{{ turfNotice.d.name }}</div>
          <div class="cm-tn-meta">
            <span>{{ turfNotice.d.family }}</span>
            <span class="cm-tn-dot">᛭</span>
            <span class="cm-tn-tier">{{ t('city.tier', { n: ROMAN[turfNotice.d.tier - 1] ?? turfNotice.d.tier }) }}</span>
            <span v-if="turfNotice.d.owner === 'you'" class="cm-tn-yours">· {{ t('city.yourGround') }}</span>
          </div>
          <div class="cm-tn-warn">
            <template v-if="turfNotice.danger === 'deadly'">{{ t('city.turf.farAbove') }}</template>
            <template v-else-if="turfNotice.danger === 'risky'">{{ t('city.turf.aboveWeight') }}</template>
            <template v-else-if="turfNotice.d.owner === 'you'">{{ t('city.turf.yourName') }}</template>
            <template v-else>{{ t('city.turf.canHandle') }}</template>
            <template v-if="turfNotice.d.locked"> {{ t('city.lockedUntil', { level: turfNotice.d.unlockLevel }) }}</template>
          </div>
        </div>
        <button class="btn-forge btn-forge--carved btn-forge-icon cm-tn-close" :title="t('city.dismiss')" @click="turfNotice = null">✕</button>
      </div>
    </Transition>

    <!-- the ground is yours — stays up until dismissed, it is worth reading -->
    <Transition name="turf-notice">
      <div v-if="takenNotice" class="cm-turf-notice cm-turf-taken bound-frame relative" role="status">
        <img v-if="takenNotice.crest" :src="takenNotice.crest" class="cm-tn-crest" alt="" />
        <div class="cm-tn-body">
          <div class="cm-tn-kicker">{{ t('city.takenKicker') }}</div>
          <div class="cm-tn-name">{{ t('city.takenTitle', { name: takenNotice.d.name }) }}</div>
          <div class="cm-tn-meta">
            <span>{{ t('city.takenFrom', { family: takenNotice.d.family }) }}</span>
            <span class="cm-tn-dot">᛭</span>
            <span class="cm-tn-tier">{{ t('city.tier', { n: ROMAN[takenNotice.d.tier - 1] ?? takenNotice.d.tier }) }}</span>
          </div>
          <div class="cm-tn-warn">
            {{ t('city.takenDesc') }}
          </div>
          <button class="btn-forge btn-forge--rune btn-forge--sm cm-tn-cta" @click="goToWards">{{ t('city.takenCta') }}</button>
        </div>
        <button class="btn-forge btn-forge--carved btn-forge-icon cm-tn-close" :title="t('city.dismiss')" @click="takenNotice = null">✕</button>
      </div>
    </Transition>

    <!-- top-left HUD plaque — nerve gauge + street plate -->
    <div class="absolute top-3 left-3 pointer-events-none select-none">
      <div class="cm-plaque bound-frame relative">
                        <div class="cm-plaque-title">{{ t('city.title') }}</div>
        <div class="cm-plaque-row flex items-center gap-3 mt-1.5">
          <div class="cm-nerve flex items-center gap-1.5" tabindex="0" role="img" :aria-label="t('city.furyOf', { current: nerve.current, max: nerve.max })">
            <img :src="nerveIcon" alt="" class="w-5 h-5 object-contain shrink-0" />
            <span class="flex items-center gap-[3px]">
              <i v-for="n in Math.min(nerve.max, 12)" :key="n" class="cm-pip" :class="{ 'cm-pip-lit': n <= nerve.current }"></i>
            </span>
            <span class="font-heading text-base text-text leading-none ml-0.5 whitespace-nowrap">{{ nerve.current }}<span class="text-text-dim">/{{ nerve.max }}</span><span v-if="nerveClock && !nerveClock.full" class="font-body text-[13px] tabular-nums text-text-dim"> · {{ fmtEta(nerveClock.nextIn) }}</span></span>
            <span class="cm-info" aria-hidden="true">i</span>
            <div class="cm-nerve-tip" role="tooltip">
              {{ t('city.furyDesc', { eta: nerveClock && !nerveClock.full ? t('city.furyDescEta', { fullIn: fmtEta(nerveClock.fullIn) }) : '' }) }}
            </div>
          </div>
          <span class="cm-divider"></span>
          <span class="cm-plate cm-loc" :title="`You're standing in ${locationName}`">&#5869; {{ locationName }}</span>
        </div>
      </div>
      <!-- status ribbon: permanent fixed-width intel line — only the text swaps,
           so the plaque column never moves a pixel -->
      <div class="cm-ribbon">
        <template v-if="error"><span class="text-error cm-ribbon-clip">{{ error }}</span></template>
        <template v-else-if="hoverName || hoverBlocked || hoverCost !== null">
          <span v-if="hoverName" class="text-gold cm-ribbon-clip">{{ hoverName }}</span>
          <!-- verdict pinned to the ribbon's right edge — label left, cost
               right, like a spec line; adrift mid-strip it read as crooked -->
          <span v-if="hoverBlocked" class="text-error shrink-0 ml-auto">{{ t('city.noWayThrough') }}</span>
          <span v-else-if="hoverCost !== null" class="cm-move shrink-0 ml-auto">{{ t('city.move') }} &middot; <span class="text-gold">{{ hoverCost }}</span><img :src="nerveIcon" :alt="t('city.furyCost')" class="cm-move-ic" /></span>
        </template>
        <span v-else class="text-text-dim/50 italic normal-case tracking-normal font-body">{{ t('city.casingIce') }}</span>
      </div>

      <!-- running on fumes? one-tap recovery, right where the gauges are -->
      <QuickRecover
        class="mt-2"
        :nerve="nerve.current" :max-nerve="nerve.max"
        :hp="props.heroHp" :max-hp="props.heroMaxHp" :full-hp="props.heroFullHp"
        :hp-regen="props.heroHpRegen" :nerve-regen="nerve.regen"
        :cash="props.cash" :gold-bars="props.goldBars"
        @updated="onRecovered"
      />
    </div>

    <p v-if="loading" class="absolute inset-0 grid place-items-center text-text-dim pointer-events-none">{{ t('city.loading') }}</p>

    <!-- center-on-hero button -->
    <button class="btn-forge btn-forge--carved btn-forge--sm absolute top-3 right-3"
            @click="centerOnHero">&#8982; {{ t('city.center') }}</button>

    <!-- map legend — "what am I looking at" -->
    <button class="btn-forge btn-forge--carved btn-forge--sm absolute top-14 right-3"
            :title="t('map.legend.button')" :aria-label="t('map.legend.button')"
            @click="showLegend = true">&#5869; {{ t('map.legend.button') }}</button>

    <!-- Minimap. It swallows its own gestures: it used to be pointer-events-none,
         so a tap on it went straight through to the map canvas underneath, which
         read it as a move order to whatever hex happened to sit behind the widget
         and answered "not enough fury" (QA 86cb8z6ex). The canvas keeps its
         handlers and is a SIBLING, so simply owning the events here is enough —
         nothing bubbles to it. A drag that STARTS on the canvas is unaffected:
         onPointerDown takes a pointer capture, so it keeps every move and the
         release wherever the cursor travels, minimap included. `cursor: default`
         because the canvas's crosshair over an inert widget was half the
         invitation to click it in the first place. -->
    <div class="absolute bottom-3 right-3 border border-border"
         style="background: rgba(9, 15, 26,0.8); box-shadow: var(--shadow-lift);
                cursor: default; touch-action: none"
         @wheel.prevent>
      <canvas ref="minimapRef" width="148" height="148" class="block"></canvas>
    </div>

    <!-- "how to run the city" — tucked under a deco help button.
         Lifted clear of the Hall pull tab, which is position:fixed at the
         bottom-left on every game screen and was sitting under this button.
         --dock-hall-clear (app.css) is the shared reservation for that tab, and
         the left offset matches its 22px so the two stack in one column. -->
    <div class="cm-help-dock absolute">
      <transition name="cm-help">
        <div v-if="showHelp" class="cm-help-card bound-frame relative mb-2">
                              <div class="cm-plaque-title mb-2">{{ t('city.help.title') }}</div>
          <ul class="space-y-1.5 font-body text-[13px] text-text-dim leading-snug">
            <li><span class="cm-help-key">&#5869;</span> <b class="cm-help-verb">{{ t('city.help.drag') }}</b> &mdash; {{ t('city.help.dragDesc') }}</li>
            <li><span class="cm-help-key">&#5869;</span> <b class="cm-help-verb">{{ t('city.help.scroll') }}</b> &mdash; {{ t('city.help.scrollDesc') }}</li>
            <li><span class="cm-help-key">&#5869;</span> <b class="cm-help-verb">{{ t('city.help.clickHex') }}</b> &mdash; {{ t('city.help.clickHexDesc') }}</li>
            <li><span class="cm-help-key">&#5869;</span> <b class="cm-help-verb">{{ t('city.help.clickEnemy') }}</b> &mdash; {{ t('city.help.clickEnemyDesc') }}</li>
            <li><span class="cm-help-key">&#5869;</span> <b class="cm-help-verb">{{ t('city.help.standStronghold') }}</b> &mdash; {{ t('city.help.standDesc') }}</li>
          </ul>
          <p class="mt-2 pt-2 border-t border-border/60 font-body italic text-[11px] text-text-dim/80">
            {{ t('city.help.clansNote') }}
          </p>
        </div>
      </transition>
      <button class="btn-forge btn-forge-icon" :class="showHelp ? 'btn-forge--rune' : 'btn-forge--carved'" :title="t('city.help.howTo')"
              @click="showHelp = !showHelp">?</button>
    </div>

    <!-- vignette frame -->
    <div class="pointer-events-none absolute inset-0"
         style="box-shadow: inset 0 0 140px 16px rgba(0,0,0,0.7)"></div>

    <!-- pre-fight calling card — details + confirm before any blood is spilled -->
    <div v-if="confirmFight" class="cm-cf-back" @click.self="confirmFight = null">
      <div class="cm-cf bound-frame relative" :class="confirmFight.kind === 'mob' ? confirmOdds?.cls : ''">
                
        <template v-if="confirmFight.kind === 'mob'">
          <div class="cm-cf-mugwrap">
            <!-- Mythic ids resolve their own portraits now; kingpin is only the
                 safety net for a mythic id with no art file. -->
            <img v-if="mobArt(confirmFight.mob.type, confirmFight.mob.archetype) ?? (confirmFight.mob.kind === 'mythic' ? mythicPortraitFallback : undefined)"
                 :src="mobArt(confirmFight.mob.type, confirmFight.mob.archetype) ?? mythicPortraitFallback"
                 class="cm-cf-mug" :class="{ 'cm-cf-mug--mythic': confirmFight.mob.kind === 'mythic' }" alt="" />
          </div>
          <div class="cm-cf-kicker">{{ t('city.fight.challenge') }}</div>
          <div class="cm-cf-name">{{ confirmFight.mob.name }}</div>
          <div class="cm-cf-meta">
            <span>{{ t('city.lv', { level: confirmFight.mob.level }) }}</span>
            <span class="cm-cf-dot">᛭</span>
            <!-- rarity on the calling card (owner 01.09) — gold for a world
                 boss, glacier ice for the hold's beast, quiet for the rest -->
            <span class="cm-cf-rar" :class="confirmFight.mob.kind === 'mythic' ? 'cm-cf-rar--mythic' : confirmFight.mob.kind === 'elite' ? 'cm-cf-rar--elite' : 'cm-cf-rar--common'">
              {{ t(`city.fight.rarity.${confirmFight.mob.kind ?? 'common'}`) }}
            </span>
            <span class="cm-cf-dot">᛭</span>
            <span class="cm-cf-odds">{{ confirmOdds?.label }}</span>
          </div>
          <div class="cm-cf-stats">
            <div><b>{{ confirmFight.mob.hp }}</b><i>{{ t('city.fight.health') }}</i></div>
            <div><b>{{ confirmFight.mob.attack }}</b><i>{{ t('city.fight.attack') }}</i></div>
            <div><b>{{ confirmFight.mob.defense }}</b><i>{{ t('city.fight.defense') }}</i></div>
          </div>
          <div class="cm-cf-pay">
            <img :src="cashIcon" alt="" /><span>{{ t('city.fight.rewardSilver', { amount: confirmFight.mob.cash.toLocaleString() }) }}</span>
            <span class="cm-cf-dot">·</span><span>{{ t('city.fight.rewardXp', { amount: confirmFight.mob.exp.toLocaleString() }) }}</span>
          </div>
          <p class="cm-cf-note">{{ confirmOdds?.note }}</p>
          <p v-if="heroHpPct !== null && heroHpPct < 40" class="cm-cf-hpwarn">
            {{ t('city.fight.lowHealth', { pct: heroHpPct }) }}
          </p>
        </template>

        <template v-else>
          <div class="cm-cf-mugwrap">
            <img v-if="crestArt(confirmFight.d.name)" :src="crestArt(confirmFight.d.name)" class="cm-cf-crest" alt="" />
          </div>
          <div class="cm-cf-kicker">{{ confirmFight.kind === 'defend' ? t('city.fight.raidersAtGate') : t('city.fight.groundTakeover') }}</div>
          <div class="cm-cf-name">{{ confirmFight.d.name }}</div>
          <div class="cm-cf-meta">
            <span>{{ confirmFight.d.family }}</span>
            <span class="cm-cf-dot">᛭</span>
            <span>{{ t('city.tier', { n: ROMAN[confirmFight.d.tier - 1] ?? confirmFight.d.tier }) }}</span>
          </div>
          <p class="cm-cf-note">
            {{ confirmFight.kind === 'defend'
              ? t('city.fight.defendNote', { family: confirmFight.d.family, name: confirmFight.d.name })
              : t('city.fight.garrison', { family: confirmFight.d.family, name: confirmFight.d.name }) }}
          </p>
          <p v-if="heroHpPct !== null && heroHpPct < 40" class="cm-cf-hpwarn">
            {{ t('city.fight.lowHealth', { pct: heroHpPct }) }}
          </p>
        </template>

        <div class="cm-cf-actions">
          <button class="btn-forge btn-forge--carved" @click="confirmFight = null">{{ t('city.fight.walkAway') }}</button>
          <button class="btn-forge btn-forge--rune cm-cf-go" :disabled="!confirmArmed" @click="onConfirmFight">
            <span>{{ confirmFight.kind === 'mob' ? t('city.fight.startFight') : confirmFight.kind === 'defend' ? t('city.fight.breakSiege') : t('city.fight.takeGround') }}</span>
            <span class="cm-cf-cost"><img :src="nerveIcon" alt="" />{{ t('city.fight.furyCost') }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- interactive tactical fight — turn-by-turn against the server -->
    <FightScreen
      v-if="tacticalTarget"
      :col="tacticalTarget.col"
      :row="tacticalTarget.row"
      :hero-class="props.heroClass"
      @done="onTacticalDone"
    />

    <!-- interactive turf takeover — same tactical arena, vs the garrison -->
    <FightScreen
      v-if="garrisonTarget"
      :garrison-id="garrisonTarget"
      :hero-class="props.heroClass"
      @done="onCaptureDone"
    />

    <!-- interactive ward defense — same arena, vs the siege warband -->
    <FightScreen
      v-if="defendTarget"
      :defend-id="defendTarget"
      :hero-class="props.heroClass"
      @done="onDefendDone"
    />

    <!-- map legend — terrain, enemies, and everything else a new player sees -->
    <div v-if="showLegend" class="cm-lg-back" @click.self="showLegend = false">
      <div class="cm-lg bound-frame relative">
        <button class="btn-forge btn-forge--carved btn-forge-icon absolute top-3 right-3 z-10"
                :aria-label="t('common.close')" @click="showLegend = false">&times;</button>

        <div class="cm-lg-head">
          <div class="cm-plaque-title">{{ t('map.legend.title') }}</div>
          <p class="cm-lg-sub">{{ t('map.legend.subtitle') }}</p>
        </div>

        <section class="cm-lg-section">
          <h3 class="cm-lg-h">{{ t('map.legend.terrainHeading') }}</h3>
          <ul class="cm-lg-terrain">
            <li v-for="tt in LEGEND_TERRAIN" :key="tt">
              <img :src="terrainSwatch(tt)" alt="" class="cm-lg-swatch" />
              <div class="cm-lg-item-text">
                <div class="cm-lg-item-name">{{ t(`map.legend.terrain.${tt}.name`) }}</div>
                <div class="cm-lg-item-desc">{{ t(`map.legend.terrain.${tt}.desc`) }}</div>
              </div>
              <span class="cm-lg-tag" :class="{ 'is-impassable': !isPassableType(tt) }">
                {{ isPassableType(tt) ? t('map.legend.costLabel', { cost: TILE_COST[tt] }) : t('map.legend.impassableLabel') }}
              </span>
            </li>
          </ul>
        </section>

        <section class="cm-lg-section">
          <h3 class="cm-lg-h">{{ t('map.legend.enemyHeading') }}</h3>
          <p class="cm-lg-p">{{ t('map.legend.enemy.markerDesc') }}</p>
          <p class="cm-lg-p">{{ t('map.legend.enemy.strengthNote') }}</p>
          <div class="cm-lg-h2">{{ t('map.legend.enemy.tierHeading') }}</div>
          <ul class="cm-lg-tiers">
            <li v-for="row in LEGEND_TIERS" :key="row.tier" :class="`is-${dangerForUnlock(row.unlockLevel)}`">
              <span class="cm-lg-tier-num">{{ ROMAN[row.tier - 1] ?? row.tier }}</span>
              <span class="cm-lg-tier-lvl">{{ t('map.legend.enemy.tierRow', { tier: ROMAN[row.tier - 1] ?? row.tier, level: row.unlockLevel }) }}</span>
              <span class="cm-lg-tier-desc">{{ t(`map.legend.enemy.tier${row.tier}`) }}</span>
              <span class="cm-lg-tier-danger">{{ t(`map.legend.enemy.danger.${dangerForUnlock(row.unlockLevel)}`) }}</span>
            </li>
          </ul>
          <p class="cm-lg-p cm-lg-mythic">
            <span class="cm-lg-mythic-dot" aria-hidden="true"></span>
            {{ t('map.legend.mythic') }}
          </p>
        </section>

        <section class="cm-lg-section">
          <h3 class="cm-lg-h">{{ t('map.legend.otherHeading') }}</h3>
          <ul class="cm-lg-other">
            <li v-for="key in LEGEND_OTHER" :key="key">
              <div class="cm-lg-item-name">{{ t(`map.legend.other.${key}.name`) }}</div>
              <div class="cm-lg-item-desc">{{ t(`map.legend.other.${key}.desc`) }}</div>
            </li>
          </ul>
        </section>

        <!-- QA 18.08 item 14 — the ice does not end here -->
        <section class="cm-lg-section cm-lg-expansion">
          <h3 class="cm-lg-h">{{ t('map.legend.expansion.title') }}</h3>
          <p class="cm-lg-p">{{ t('map.legend.expansion.body') }}</p>
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ── map HUD deco ─────────────────────────────────────────────── */
.cm-plaque {
  /* z-index lifts the plaque (and its Nerve tooltip) above the QuickRecover
     pill below — both create stacking contexts (backdrop-filter here, the
     pulsing drop-shadow there), so without this the pill pokes through the tip */
  position: relative; z-index: 30;
  /* SAME width as the ribbon below — the two used to size independently
     (plaque by content, ribbon fixed) and the mismatched right edges read
     as crooked (owner). The location plate ellipsizes into whatever is
     left, so a long ward name can't push past the fixed edge. */
  /* +15-20% per QA 18.08 item 3 — the whole plaque cluster steps up together
     (title, pips, plates, ribbon) so the HUD stays one instrument. */
  width: 435px;
  padding: 0.65rem 1.05rem 0.75rem;
  border: 1px solid color-mix(in srgb, var(--color-gold) 35%, transparent);
  border-radius: 0;
  background: linear-gradient(180deg, rgba(16, 25, 37,0.92), rgba(9, 15, 24,0.88));
  box-shadow: 0 4px 18px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.04);
  backdrop-filter: blur(3px);
}
.cm-plaque-title {
  font-family: var(--font-heading, serif);
  font-size: 17px; line-height: 1; color: var(--color-gold);
  letter-spacing: 0.06em;
}
.cm-pip {
  width: 8px; height: 8px; transform: rotate(45deg); border-radius: 1px;
  background: rgba(255,255,255,0.08); border: 1px solid rgba(200,162,76,0.35);
  transition: background 200ms ease, box-shadow 200ms ease;
}
.cm-pip-lit {
  background: linear-gradient(135deg, var(--color-gold-bright), var(--color-gold));
  box-shadow: 0 0 5px rgba(227,200,120,0.5);
}
.cm-divider { width: 1px; height: 19px; background: linear-gradient(180deg, transparent, var(--color-border), transparent); }
/* nerve gauge — hoverable helper tooltip (the plaque wrapper is pointer-events:none) */
.cm-nerve { position: relative; pointer-events: auto; cursor: help; }
.cm-info {
  display: inline-grid; place-items: center; width: 16px; height: 16px; margin-left: 3px;
  font-family: var(--font-label, sans-serif); font-size: 9px; font-style: italic; line-height: 1;
  color: var(--color-gold); border: 1px solid color-mix(in srgb, var(--color-gold) 50%, transparent);
  border-radius: 0;
}
.cm-nerve-tip {
  position: absolute; top: calc(100% + 9px); left: 0; z-index: 40;
  width: 244px; padding: 10px 12px;
  font-family: var(--font-body, serif); font-size: 12.5px; letter-spacing: normal; text-transform: none;
  line-height: 1.45; color: var(--color-text-dim);
  background: var(--color-surface-2, #100e14);
  border: 1px solid color-mix(in srgb, var(--color-gold) 45%, transparent);
  border-radius: 0; box-shadow: 0 8px 22px rgba(0,0,0,0.6);
  opacity: 0; visibility: hidden; transform: translateY(-4px);
  transition: opacity 140ms ease, transform 140ms ease, visibility 140ms;
}
.cm-nerve-tip::before {
  content: ''; position: absolute; bottom: 100%; left: 14px;
  border: 6px solid transparent; border-bottom-color: color-mix(in srgb, var(--color-gold) 45%, transparent);
}
.cm-nerve-tip b { color: var(--color-gold-bright); font-weight: 400; }
.cm-nerve:hover .cm-nerve-tip, .cm-nerve:focus-visible .cm-nerve-tip { opacity: 1; visibility: visible; transform: none; }
/* move-cost readout — keep the number and its icon on one line */
.cm-move { display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; }
.cm-move-ic { width: 12px; height: 12px; object-fit: contain; flex: none; }
.cm-atmo {
  position: absolute; inset: 0; z-index: 5; pointer-events: none;
  background:
    var(--orn-frost-lg-ice) repeat-x left top,
    radial-gradient(46% 34% at 22% 0%, rgba(111, 216, 192, 0.10), transparent 70%),
    radial-gradient(40% 30% at 66% 0%, rgba(111, 176, 232, 0.08), transparent 68%),
    radial-gradient(130% 105% at 50% 46%, transparent 52%, rgba(7, 12, 21, 0.36) 78%, rgba(5, 9, 17, 0.72) 100%);
}

/* the HUD readouts. Were 1px-bordered translucent pills on a 3%-white wash — the
   generic "stat chip". They are slots cut into the hold's ironwork now, the same
   grammar as the header's coin slots. */
.cm-plate {
  font-family: var(--font-label, sans-serif); font-size: 13px; letter-spacing: 0.06em;
  color: #bccbdb;
  padding: 3px 9px;
  border: 0; border-radius: 0;
  clip-path: var(--clip-hewn);
  background: var(--sunken);
  box-shadow: var(--rim-ice-sunken);
  font-variant-numeric: tabular-nums;
}
/* where you are standing is the one warm readout on the map */
.cm-loc {
  min-width: 0; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  color: var(--color-gold-bright); letter-spacing: 0.05em;
  box-shadow: var(--rim-ice-sunken), inset 0 0 14px color-mix(in srgb, var(--color-ember) 22%, transparent);
}
/* ── MOBILE HUD ── the plaque + ribbon shrink to leave room for ⌖ Center,
   and the nerve/location row is allowed to wrap inside the plaque */
@media (max-width: 640px) {
  .cm-plaque, .cm-ribbon { width: calc(100vw - 122px); max-width: 435px; }
  .cm-plaque-row { flex-wrap: wrap; row-gap: 4px; }
  .cm-plaque-row .cm-divider { display: none; }
  .cm-loc { max-width: 100%; }
}
/* the ribbon is a beam offcut: timber with the cord chased along it, not a gradient
   bar with a gold rule down one side. (Its `border-radius: 0 4px 4px 0` also slipped
   past the app-wide radius sweep, which skipped multi-value forms.) */
.cm-ribbon {
  position: relative;
  display: flex; align-items: center; gap: 0.9rem;
  height: 33px; width: 435px; margin-top: 6px; padding: 0 14px;
  font-family: var(--font-label, sans-serif); font-size: 13.5px; letter-spacing: 0.06em; text-transform: uppercase;
  /* brighter ink + a hard drop — the old #bccbdb at 11px sank into the beam
     (owner: hard to read); the timber gets a dark wash under the texture so
     the letters carry the light, not the wood grain */
  color: #dfe9f4;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.85);
  border: 0; border-radius: 0;
  background:
    linear-gradient(180deg, rgba(7, 10, 16, 0.38), rgba(7, 10, 16, 0.5)),
    var(--tex-forge), var(--mat-beam);
  background-size: auto, 150px 150px, auto;
  background-blend-mode: normal, soft-light, normal;
  box-shadow: var(--rim-ice), 0 6px 16px rgba(0, 0, 0, 0.5);
  white-space: nowrap; overflow: hidden;
}
.cm-ribbon::after {
  content: ''; position: absolute; inset: 0; pointer-events: none; opacity: 0.4;
  background:
    var(--orn-cord-dark) repeat-x left top 1px,
    var(--orn-cord-lite) repeat-x left top 2px,
    var(--orn-cord-dark) repeat-x left bottom 2px,
    var(--orn-cord-lite) repeat-x left bottom 1px;
}
.cm-ribbon-clip { overflow: hidden; text-overflow: ellipsis; min-width: 0; }
/* the error ink needs the same brightening the base ink already got: raw
   --color-error (#b3263a) reads as a stain on the dark timber, not a warning
   (owner: 'very darkened, unreadable') */
.cm-ribbon .text-error { color: #f5968a; text-shadow: 0 1px 1px rgba(0, 0, 0, 0.9); }
/* The MOBILE HUD block above narrows the plaque and the ribbon together, but
   `.cm-ribbon { width: 372px }` is declared BELOW it at equal specificity, so
   source order won and only the plaque ever shrank — the ribbon ran 9px past a
   375px screen and 64px past a 320px one, cutting off the move-cost verdict
   pinned to its right edge. Re-stated here, after the base rule. */
@media (max-width: 640px) {
  .cm-ribbon { width: calc(100vw - 122px); max-width: 435px; }
}
.cm-help-verb { color: var(--color-text); font-weight: 600; }
/* Bottom-left corner dock. Must clear the Hall pull tab (fixed, bottom-left, on
   every game screen) — see --dock-hall-clear in app.css. Left is 22px to line up
   with .hall-dock rather than the map's own 12px inset, so the button and the tab
   read as one stacked column instead of two things that nearly collide. */
.cm-help-dock {
  left: 22px;
  bottom: calc(var(--dock-hall-clear, 46px) + 8px);
}

.cm-help-card {
  width: 300px;
  /* 1.4rem of top room clears .bound-frame's own strap + frost fringe */
  padding: 1.4rem 1rem 1rem;
  border: 0; border-radius: 0;
  background: var(--surface-plank);
  background-size: var(--surface-plank-size);
  background-blend-mode: var(--surface-plank-blend);
  box-shadow: var(--rim-ice), 0 12px 34px rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
}
.cm-help-key { display: inline-block; width: 1.4em; color: var(--color-aurora); }
.cm-help-enter-active, .cm-help-leave-active { transition: opacity 180ms ease, transform 180ms ease; }
.cm-help-enter-from, .cm-help-leave-to { opacity: 0; transform: translateY(6px); }
@media (prefers-reduced-motion: reduce) {
  .cm-help-enter-active, .cm-help-leave-active { transition: none; }
}

/* the conquest card wears the win colour and carries a call to action */
.cm-turf-taken { --tn: var(--color-gold); }
.cm-tn-cta { margin-top: 10px; align-self: flex-start; }

/* ── territory-entry calling card ─────────────────────────────────── */
.cm-turf-notice {
  /* safe ground cold, risky ember, deadly blood — the game's one danger scale */
  --tn: var(--color-aurora);
  position: absolute;
  top: 14px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 14px;
  max-width: min(480px, calc(100% - 24px));
  /* 20px of top room clears .bound-frame's strap + frost */
  padding: 20px 16px 14px;
  background: var(--surface-plank);
  background-size: var(--surface-plank-size);
  background-blend-mode: var(--surface-plank-blend);
  border: 0;
  box-shadow:
    inset 0 1px 0 color-mix(in srgb, var(--tn) 40%, rgba(207, 232, 255, 0.34)),
    inset 0 0 0 1px color-mix(in srgb, var(--tn) 30%, transparent),
    inset 0 0 26px color-mix(in srgb, var(--tn) 14%, transparent),
    0 18px 50px rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
}
.cm-turf-notice { --cb: var(--tn); }  /* tints the frame rim (was the cleats) */
.cm-turf-notice.is-risky { --tn: var(--color-ember); }
.cm-turf-notice.is-deadly { --tn: var(--color-error); }

.cm-tn-crest {
  width: 52px;
  height: 52px;
  flex: none;
  object-fit: contain;
  filter: drop-shadow(0 3px 8px rgba(0, 0, 0, 0.6));
}
.cm-tn-kicker {
  font-family: var(--font-label);
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--color-text-dim);
}
.cm-tn-name {
  font-family: var(--font-heading);
  font-size: 21px;
  line-height: 1.15;
  color: var(--color-text);
}
.cm-tn-meta {
  display: flex;
  align-items: baseline;
  gap: 7px;
  margin-top: 1px;
  font-family: var(--font-label);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: color-mix(in srgb, var(--tn) 85%, var(--color-text));
}
.cm-tn-dot { font-size: 7px; color: var(--tn); }
.cm-tn-tier { color: var(--color-gold-bright); }
.cm-tn-yours { color: var(--color-gold-bright); }
.cm-tn-warn {
  margin-top: 5px;
  font-family: var(--font-body);
  font-size: 13.5px;
  line-height: 1.35;
  color: color-mix(in srgb, var(--tn) 55%, var(--color-text));
}
.cm-tn-close { flex: none; align-self: flex-start; margin-left: 2px; }

.turf-notice-enter-active { transition: opacity 300ms ease, transform 300ms ease; }
.turf-notice-leave-active { transition: opacity 240ms ease, transform 240ms ease; }
.turf-notice-enter-from { opacity: 0; transform: translate(-50%, -14px); }
.turf-notice-leave-to { opacity: 0; transform: translate(-50%, -8px); }
@media (prefers-reduced-motion: reduce) {
  .turf-notice-enter-active, .turf-notice-leave-active { transition: none; }
}

/* ── pre-fight calling card ─────────────────────────────────────────────── */
.cm-cf-back {
  position: absolute; inset: 0; z-index: 60;
  display: grid; place-items: center; padding: 16px;
  background: rgba(6, 4, 12, 0.72);
  backdrop-filter: blur(3px); -webkit-backdrop-filter: blur(3px);
}
.cm-cf {
  /* +25% per QA 18.08 item 4 — the card and everything on it, so phone
     thumbs get real targets and the mug/name read at arm's length. */
  width: min(475px, 94%);
  padding: 26px 26px 22px;
  text-align: center;
  border: 1px solid color-mix(in srgb, var(--color-gold, #c9a84c) 45%, transparent);
  border-radius: 0;
  background:
    radial-gradient(120% 90% at 50% 0%, rgba(200, 162, 76, 0.10), transparent 55%),
    var(--color-surface, #14121a);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.7);
  animation: cm-cf-rise 220ms ease both;
}
@keyframes cm-cf-rise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .cm-cf { animation: none; } }
.cm-cf.is-risky { border-color: color-mix(in srgb, #d99a3d 55%, transparent); }
.cm-cf.is-deadly { border-color: color-mix(in srgb, var(--color-error, #b3413e) 65%, transparent); }
.cm-cf-mugwrap { display: flex; justify-content: center; margin-bottom: 8px; }
.cm-cf-mug {
  width: 105px; height: 105px; border-radius: 0; object-fit: cover; object-position: top;
  border: 2px solid color-mix(in srgb, var(--color-gold, #c9a84c) 70%, transparent);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.6);
  background: #0c0a11;
}
/* MYTHIC world boss — gold/aurora glow ring on the calling-card portrait,
   the DOM-side twin of drawMythicRing() on the canvas board. */
.cm-cf-mug--mythic {
  border-color: #e8b73f;
  box-shadow: 0 0 0 3px rgba(232, 183, 63, 0.35), 0 0 22px 6px rgba(160, 255, 225, 0.28), 0 6px 18px rgba(0, 0, 0, 0.6);
}
.cm-cf-crest {
  width: 105px; height: 105px; object-fit: contain;
  filter: drop-shadow(0 6px 14px rgba(0, 0, 0, 0.65));
}
.cm-cf-kicker { font-family: var(--font-label, sans-serif); text-transform: uppercase; letter-spacing: 0.09em; font-size: 12px; color: var(--color-text-dim, #9a8f7a); }
.cm-cf-name { font-family: var(--font-heading, serif); font-size: 32px; line-height: 1.15; color: var(--color-gold-bright, #e9c97a); margin: 2px 0 3px; }
.cm-cf-meta { display: flex; justify-content: center; align-items: center; gap: 8px; font-family: var(--font-label, sans-serif); text-transform: uppercase; letter-spacing: 0.07em; font-size: 13px; color: var(--color-text-dim, #9a8f7a); }
.cm-cf-rar--mythic { color: #f0dda4; text-shadow: 0 0 8px rgba(240, 221, 164, 0.45); }
.cm-cf-rar--elite { color: #9fe0ff; text-shadow: 0 0 8px rgba(159, 224, 255, 0.4); }
.cm-cf-rar--common { color: var(--color-text-dim, #9a8f7a); }
.cm-cf-dot { color: var(--color-gold, #c9a84c); font-size: 11px; }
.is-risky .cm-cf-odds { color: #d99a3d; }
.is-deadly .cm-cf-odds { color: var(--color-error, #b3413e); }
.is-safe .cm-cf-odds { color: var(--color-success, #7fb79c); }
.cm-cf-stats {
  display: grid; grid-template-columns: repeat(3, 1fr);
  margin: 14px 0 0;
  border: 1px solid var(--color-border, #2a2436); border-radius: 0; overflow: hidden;
  background: rgba(0, 0, 0, 0.2);
}
.cm-cf-stats > div { display: flex; flex-direction: column; gap: 2px; padding: 10px 5px; }
.cm-cf-stats > div + div { border-left: 1px solid var(--color-border, #2a2436); }
.cm-cf-stats b { font-family: var(--font-heading, serif); font-weight: 600; font-size: 20px; color: var(--color-text, #ece3cf); line-height: 1.1; }
.cm-cf-stats i { font-style: normal; font-family: var(--font-label, sans-serif); font-size: 11px; text-transform: uppercase; letter-spacing: 0.07em; color: var(--color-text-dim, #9a8f7a); }
.cm-cf-pay {
  display: flex; justify-content: center; align-items: center; gap: 7px;
  margin-top: 11px; font-family: var(--font-label, sans-serif); font-size: 14px;
  letter-spacing: 0.06em; color: var(--color-gold, #c9a84c);
}
.cm-cf-pay img { width: 20px; height: 20px; object-fit: contain; }
.cm-cf-note { font-family: var(--font-body, serif); font-style: italic; font-size: 16px; color: var(--color-text-dim, #9a8f7a); margin-top: 10px; line-height: 1.45; }
.cm-cf-hpwarn { font-family: var(--font-body, serif); font-size: 15px; color: var(--color-error, #b3413e); margin-top: 6px; }
/* wrap: the nowrap plaques ("Start the fight · 1 nerve") can outgrow the card
   on phones — let them stack to full-width rows instead of overflowing it */
.cm-cf-actions { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; margin-top: 16px; }
.cm-cf-actions > * { flex: 1 1 auto; }
/* the fight/takeover button states its nerve price so the spend is never a surprise */
.cm-cf-go { display: inline-flex; align-items: center; gap: 9px; }
.cm-cf-cost {
  display: inline-flex; align-items: center; gap: 3px;
  padding-left: 9px; border-left: 1px solid color-mix(in srgb, currentColor 35%, transparent);
  font-family: var(--font-label, monospace); font-size: 12px; letter-spacing: 0.06em;
  opacity: 0.92;
}
.cm-cf-cost img { width: 15px; height: 15px; object-fit: contain; }

/* ── map legend ────────────────────────────────────────────────────────── */
.cm-lg-back {
  position: absolute; inset: 0; z-index: 70;
  display: grid; place-items: center; padding: 16px;
  background: rgba(6, 4, 12, 0.78);
  backdrop-filter: blur(3px); -webkit-backdrop-filter: blur(3px);
}
.cm-lg {
  width: min(620px, 100%);
  max-height: min(88vh, 720px);
  overflow-y: auto;
  padding: 22px 20px 20px;
  border: 0; border-radius: 0;
  background: var(--surface-plank);
  background-size: var(--surface-plank-size);
  background-blend-mode: var(--surface-plank-blend);
  box-shadow: var(--rim-ice), 0 24px 60px rgba(0, 0, 0, 0.7);
  scrollbar-width: thin;
  scrollbar-color: color-mix(in srgb, var(--color-gold) 35%, transparent) transparent;
}
.cm-lg-head { margin-bottom: 14px; padding-right: 28px; }
.cm-lg-sub { margin-top: 3px; font-family: var(--font-body, serif); font-style: italic; font-size: 12.5px; color: var(--color-text-dim); }
.cm-lg-section { margin-top: 18px; padding-top: 14px; border-top: 1px solid color-mix(in srgb, var(--color-border) 70%, transparent); }
.cm-lg-section:first-of-type { margin-top: 0; padding-top: 0; border-top: 0; }
.cm-lg-h {
  font-family: var(--font-label, sans-serif); text-transform: uppercase; letter-spacing: 0.08em;
  font-size: 11.5px; font-weight: 700; color: var(--color-gold); margin: 0 0 9px;
}
.cm-lg-h2 { font-family: var(--font-label, sans-serif); text-transform: uppercase; letter-spacing: 0.07em; font-size: 10.5px; color: var(--color-text-dim); margin: 12px 0 7px; }
.cm-lg-p { font-family: var(--font-body, serif); font-size: 13px; line-height: 1.5; color: var(--color-text-dim); margin: 0 0 7px; }

.cm-lg-terrain, .cm-lg-other { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
.cm-lg-terrain > li { display: flex; align-items: center; gap: 10px; }
.cm-lg-swatch {
  flex: none; width: 34px; height: 46px; object-fit: contain; object-position: bottom;
  background: rgba(0,0,0,0.28);
  filter: drop-shadow(0 3px 5px rgba(0,0,0,0.5));
}
.cm-lg-item-text { flex: 1; min-width: 0; }
.cm-lg-item-name { font-family: var(--font-heading, serif); font-size: 14px; color: var(--color-text); line-height: 1.2; }
.cm-lg-item-desc { font-family: var(--font-body, serif); font-size: 12px; color: var(--color-text-dim); line-height: 1.35; margin-top: 1px; }
.cm-lg-tag {
  flex: none; font-family: var(--font-label, sans-serif); font-size: 9.5px; letter-spacing: 0.05em;
  text-transform: uppercase; color: var(--color-text-dim);
  padding: 3px 8px; border: 1px solid var(--color-border); white-space: nowrap;
}
.cm-lg-tag.is-impassable { color: var(--color-error); border-color: color-mix(in srgb, var(--color-error) 55%, transparent); }

.cm-lg-tiers { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 5px; }
.cm-lg-tiers > li {
  --tn: var(--color-success, #7fb79c);
  display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap;
  padding: 6px 9px; border-left: 2px solid var(--tn);
  background: color-mix(in srgb, var(--tn) 8%, transparent);
}
.cm-lg-tiers > li.is-risky { --tn: #d99a3d; }
.cm-lg-tiers > li.is-deadly { --tn: var(--color-error, #b3413e); }
/* MYTHIC callout — gold/aurora dot previews the same ring the board + calling
   card use, so the legend visually matches what a player sees in the field. */
.cm-lg-mythic { display: flex; align-items: center; gap: 8px; margin-top: 8px; }
.cm-lg-mythic-dot {
  flex: 0 0 auto; width: 12px; height: 12px; border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #fff2c0, #e8b73f 55%, #a9720f 100%);
  box-shadow: 0 0 8px 2px rgba(160, 255, 225, 0.35);
}
.cm-lg-tier-num { font-family: var(--font-heading, serif); font-size: 13px; color: var(--color-gold-bright); flex: none; }
.cm-lg-tier-lvl { font-family: var(--font-label, sans-serif); font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; color: var(--color-text-dim); flex: none; }
.cm-lg-tier-desc { font-family: var(--font-body, serif); font-size: 12px; color: var(--color-text); flex: 1 1 160px; min-width: 0; }
.cm-lg-tier-danger { font-family: var(--font-label, sans-serif); font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--tn); flex: none; margin-left: auto; }

@media (max-width: 480px) {
  .cm-lg { padding: 18px 14px 16px; }
  .cm-lg-terrain > li { align-items: flex-start; }
  .cm-lg-tier-danger { margin-left: 0; }
}
</style>
